import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileArchive,
  FileText,
  Plus,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { collection, doc, getDoc, getDocs, setDoc } from "firebase/firestore";
import JSZip from "jszip";
import pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import useCtx from "../hooks/useCts";
import { db } from "../firebase";
import directPressLogo from "../assets/dp_logo.svg";
import magicVillageHorizontal from "../assets/magic-village-horizontal.png";
import kidsBook2Horizontal from "../assets/horizontal2.jpg";
import kidsBook3Horizontal from "../assets/horizontal3.png";
import kidsBook4Horizontal from "../assets/horizontal4.png";
import kidsBook5Horizontal from "../assets/horizontal5.jpg";
import kidsBook1Vertical from "../assets/vertical1.png";
import kidsBook2Vertical from "../assets/vertical2.jpg";
import kidsBook3Vertical from "../assets/vertical3.png";
import kidsBook4Vertical from "../assets/vertical4.png";
import kidsBook5Vertical from "../assets/vertical5.jpg";
import adultsHorizontal from "../assets/adults_hor.png";
import adultsVertical from "../assets/adults_vert.jpg";
import certificateKidsPreview from "../assets/Certificate_kids.png";
import certificateAdultsPreview from "../assets/Certificate_adults.png";
import {
  adultBookColors,
  adultBookCovers,
  kidsBookColors,
  kidsBookCovers,
  kidsBookTitles,
} from "../utils/bookmarkGroupData";
import {
  ACTION_BUTTON_TEXT_CLASS_NAME,
  getActionButtonStyle,
} from "../utils/actionButtonStyles";
import { trackEventWithoutThrottle } from "../utils/adminTracking";
import { getCertificateLogoForClient } from "../utils/certificateLogo";

const pdfFontsVfs =
  (pdfFonts as any).pdfMake?.vfs ??
  (pdfFonts as any).vfs ??
  (pdfFonts as any).default?.pdfMake?.vfs ??
  (pdfFonts as any).default?.vfs;

if (pdfFontsVfs) {
  (pdfMake as any).vfs = pdfFontsVfs;
}

type CertificateFlow = "kids" | "adults";
type CertificateOrientation = "landscape" | "portrait";

type StudentRow = {
  id: string;
  firstName: string;
  surname: string;
};

type CertificateStudent = {
  id: string;
  firstName: string;
  surname: string;
  firstNameNormalized: string;
  surnameNormalized: string;
  createdAt: number;
  createdByAdminUid?: string;
};

type BookOption = {
  id: string;
  code: string;
  title: string;
  level: string;
  sortOrder: number;
  flow: CertificateFlow;
  coverSrc?: string;
};

type ReviewItem = {
  id: string;
  firstName: string;
  surname: string;
};

type CertificateData = {
  flow: CertificateFlow;
  orientation: CertificateOrientation;
  issueDate: string;
  place: string;
  schoolName: string;
  book: BookOption;
  issuerName: string;
  schoolLogoDataUrl: string | null;
  directPressLogoDataUrl: string | null;
  directPressLogoSvg: string | null;
};

type Message = {
  type: "success" | "error" | "info" | "warning";
  text: string;
};

type CertificateImageData = {
  flow: CertificateFlow;
  studentName: string;
  completion: string;
  courseDuration: string;
  level: string;
  levelTitle: string;
  bookTitle: string;
  schoolName: string;
  schoolLogoSrc: string | null;
  publisherLogoSrc: string | null;
  city: string;
  date: string;
  signerName: string;
  signerRole: string;
  palette: string[];
  accentColors: CertificateAccentColors;
};

type CertificateAccentColors = {
  barPrimary: string;
  barSecondary: string;
  barDark: string;
  subtitle: string;
  level: string;
};

const MAX_KIDS_BOOK_NUM = 5;
const MAX_ADULT_BOOK_NUM = 7;
const KIDS_BOOK_LEVELS: Record<number, string> = {
  1: "pre A1",
  2: "A1-",
  3: "A1",
  4: "A1+/A2-",
  5: "A2",
};
const ADULT_BOOK_LEVELS: Record<number, string> = {
  1: "A1/A1+",
  2: "A1+/A2",
  3: "A2/A2+",
  4: "A2+/B1",
  5: "B1/B1+",
  6: "B1+/B2",
  7: "B2/B2+",
};
const CD_W = 1200;
const CD_H = 849;
const CD_PORT_W = 849;
const CD_PORT_H = 1200;
const CD_MARGIN = 22;
const MAGIC_VILLAGE_PALETTE = ["#f59e0b", "#38bdf8", "#f8c44f", "#15140f"];
const DEFAULT_CERTIFICATE_ACCENT_COLORS: CertificateAccentColors = {
  barPrimary: "#f59e0b",
  barSecondary: "#38bdf8",
  barDark: "#15140f",
  subtitle: "#15140f",
  level: "#f59e0b",
};
const ADULT_CERTIFICATE_DIVIDER_COLORS = ["#aeb4bd", "#858d99", "#5f6875"];
const KIDS_CERTIFICATE_ACCENT_COLORS: Record<string, CertificateAccentColors> =
  {
    K1: {
      barPrimary: "#795ba7",
      barSecondary: "#f2c76b",
      barDark: "#322146",
      subtitle: "#795ba7",
      level: "#795ba7",
    },
    K2: {
      barPrimary: "#2d9d78",
      barSecondary: "#e94f60",
      barDark: "#115d66",
      subtitle: "#2d9d78",
      level: "#2d9d78",
    },
    K3: {
      barPrimary: "#47b3a9",
      barSecondary: "#f39861",
      barDark: "#2f555d",
      subtitle: "#47b3a9",
      level: "#47b3a9",
    },
    K4: {
      barPrimary: "#2b4e78",
      barSecondary: "#eddcc2",
      barDark: "#0c275a",
      subtitle: "#2b4e78",
      level: "#2b4e78",
    },
    K5: {
      barPrimary: "#f87a4a",
      barSecondary: "#79bbad",
      barDark: "#7a2b23",
      subtitle: "#f87a4a",
      level: "#f87a4a",
    },
  };
const MAGIC_VILLAGE_EXPORT_SCALE = 3;
const CERTIFICATE_FONT_CSS_URL =
  "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@500;600&family=Caveat:wght@500;600&family=DM+Sans:wght@400;500;600;700&family=Fira+Sans:wght@500&family=Fraunces:ital,wght@1,500&display=swap";
let embeddedCertificateFontCssPromise: Promise<string> | null = null;
const DEFAULT_PREVIEW_STUDENT = {
  firstName: "YOUR",
  surname: "STUDENT",
};
const ISSUER_NAME_VALIDATION_MESSAGE =
  "Imię i nazwisko osoby wystawiającej jest wymagane.";
const NO_STUDENT_VALIDATION_MESSAGE = "Dodaj przynajmniej jednego ucznia.";
const INCOMPLETE_STUDENT_VALIDATION_MESSAGE =
  "Uzupełnij imię i nazwisko ucznia.";

const createLocalId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const createStudentRow = (): StudentRow => ({
  id: createLocalId(),
  firstName: "",
  surname: "",
});

const normalizeSpaces = (value: string) => value.trim().replace(/\s+/g, " ");

const normalizeStudentValue = (value: string) =>
  normalizeSpaces(value).toLocaleLowerCase("pl-PL");

// Existing Firestore path kept for backwards compatibility with saved students.
const CERTIFICATE_STUDENTS_COLLECTION = "dyplomyStudents";

const getApproxTextUnits = (value: string) =>
  Array.from(normalizeSpaces(value)).reduce((total, character) => {
    if (/\s/.test(character)) return total + 0.35;
    if (/[mwMWĄĆĘŁŃÓŚŹŻąćęłńóśźż]/.test(character)) return total + 0.78;
    if (/[ijlIft]/.test(character)) return total + 0.38;
    return total + 0.58;
  }, 0);

const getResponsiveStudentNameFontSize = (value: string, maxWidth: number) => {
  const maxFontSize = 60;
  const minFontSize = 30;
  const textUnits = getApproxTextUnits(value);

  if (!textUnits) return maxFontSize;

  return Math.max(
    minFontSize,
    Math.min(maxFontSize, Math.floor(maxWidth / textUnits)),
  );
};

const shouldCondenseStudentName = (
  value: string,
  fontSize: number,
  maxWidth: number,
) => getApproxTextUnits(value) * fontSize > maxWidth;

const getBookNumberFromCode = (bookCode?: string) => {
  const match = bookCode?.match(/\d+/);
  return match ? Number(match[0]) : null;
};

const getEnglishUnlockedLevelColor = (bookCode?: string) => {
  const bookNumber = getBookNumberFromCode(bookCode);
  if (!bookNumber) return null;

  if (bookCode?.startsWith("EU")) {
    return adultBookColors[bookNumber] ?? null;
  }

  if (bookCode?.startsWith("K")) {
    return kidsBookColors[bookNumber] ?? null;
  }

  return null;
};

const getCertificateAccentColors = (bookCode?: string) => {
  const baseColors =
    (bookCode && KIDS_CERTIFICATE_ACCENT_COLORS[bookCode]) ||
    DEFAULT_CERTIFICATE_ACCENT_COLORS;
  const englishUnlockedLevelColor = getEnglishUnlockedLevelColor(bookCode);

  return {
    ...baseColors,
    level: englishUnlockedLevelColor ?? baseColors.level,
  };
};

const getMagicVillageTitleParts = (bookTitle: string, levelTitle: string) => ({
  prefix: normalizeSpaces(bookTitle),
  magicVillage: normalizeSpaces(levelTitle),
});

const getStudentKey = (firstName: string, surname: string) =>
  `${normalizeStudentValue(firstName)}__${normalizeStudentValue(surname)}`;

const createCertificateStudentId = (firstName: string, surname: string) =>
  `${encodeURIComponent(normalizeStudentValue(firstName))}__${encodeURIComponent(
    normalizeStudentValue(surname),
  )}`;

const formatDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const buildEmbeddedCertificateFontCss = async () => {
  if (!embeddedCertificateFontCssPromise) {
    embeddedCertificateFontCssPromise = (async () => {
      const cssResponse = await fetch(CERTIFICATE_FONT_CSS_URL);
      if (!cssResponse.ok) throw new Error("certificate-font-css-fetch-failed");
      let css = await cssResponse.text();
      const fontUrls = Array.from(
        new Set(
          Array.from(css.matchAll(/url\((https:[^)]+)\)/g)).map(
            (match) => match[1],
          ),
        ),
      );

      for (const fontUrl of fontUrls) {
        const fontResponse = await fetch(fontUrl);
        if (!fontResponse.ok) continue;
        const dataUrl = await blobToDataUrl(await fontResponse.blob());
        css = css.replaceAll(fontUrl, dataUrl);
      }

      return css;
    })();
  }

  return embeddedCertificateFontCssPromise;
};

const blobToText = (blob: Blob) => blob.text();

const imageBlobToPngDataUrl = (blob: Blob, maxWidth = 900) =>
  new Promise<string>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = () => {
      try {
        const naturalWidth = image.naturalWidth || image.width;
        const naturalHeight = image.naturalHeight || image.height;
        const scale = naturalWidth > maxWidth ? maxWidth / naturalWidth : 1;
        const width = Math.max(1, Math.round(naturalWidth * scale));
        const height = Math.max(1, Math.round(naturalHeight * scale));
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        if (!naturalWidth || !naturalHeight || !context) {
          throw new Error("direct-press-logo-rasterize-failed");
        }

        canvas.width = width;
        canvas.height = height;
        context.clearRect(0, 0, width, height);
        context.drawImage(image, 0, 0, width, height);
        resolve(canvas.toDataURL("image/png"));
      } catch (error) {
        reject(error);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("direct-press-logo-load-failed"));
    };
    image.src = objectUrl;
  });

const urlToImageAssets = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error("image-fetch-failed");
  const blob = await response.blob();

  if (blob.type.includes("svg")) {
    const svg = await blobToText(blob);
    return {
      dataUrl: await imageBlobToPngDataUrl(blob),
      svg,
    };
  }

  return {
    dataUrl: await blobToDataUrl(blob),
    svg: null,
  };
};

const formatPolishDate = (dateValue: string) => {
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateValue;

  return date.toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const sanitizeFilenamePart = (value: string) => {
  const normalized = normalizeSpaces(value)
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, "_")
    .slice(0, 90);

  return normalized || "brak";
};

const getIssueYear = (issueDate: string) => {
  const year = issueDate.slice(0, 4);
  return /^\d{4}$/.test(year) ? year : String(new Date().getFullYear());
};

const getOrientationFilenameCode = (orientation: CertificateOrientation) =>
  orientation === "portrait" ? "ver" : "hor";

const getFlowConfig = (flow: CertificateFlow) =>
  flow === "kids"
    ? {
        title: "Certyfikaty dla dzieci",
        documentTitle: "Certyfikat",
        documentSingular: "certyfikat",
        documentPluralGenitive: "certyfikatów",
        zipSuffix: "certyfikatow",
        studentLabel: "Uczniowie",
      }
    : {
        title: "Certyfikaty dla młodzieży/dorosłych",
        documentTitle: "Certyfikat",
        documentSingular: "certyfikat",
        documentPluralGenitive: "certyfikatów",
        zipSuffix: "certyfikatow",
        studentLabel: "Uczestnicy",
      };

const escapeSvgText = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const buildSchoolNameLogoSvg = (schoolName: string) => {
  const label = escapeSvgText(normalizeSpaces(schoolName) || "Szkoła");

  return `
    <svg width="2220" height="116" viewBox="0 0 220 116" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="218" height="114" rx="20" fill="#ffffff" stroke="#cbd5e1" stroke-width="2"/>
      <text x="110" y="51" text-anchor="middle" font-family="Roboto, Arial" font-size="13" font-weight="700" fill="#0f172a">${label}</text>
      <text x="110" y="72" text-anchor="middle" font-family="Roboto, Arial" font-size="9" fill="#64748b">logo szkoły</text>
    </svg>
  `;
};

const getIssuerName = (data: { issuerName: string }) =>
  normalizeSpaces(data.issuerName);

const KIDS_IMAGE_TEMPLATE_SOURCES = {
  K1: {
    landscape: magicVillageHorizontal,
    portrait: kidsBook1Vertical,
  },
  K2: {
    landscape: kidsBook2Horizontal,
    portrait: kidsBook2Vertical,
  },
  K3: {
    landscape: kidsBook3Horizontal,
    portrait: kidsBook3Vertical,
  },
  K4: {
    landscape: kidsBook4Horizontal,
    portrait: kidsBook4Vertical,
  },
  K5: {
    landscape: kidsBook5Horizontal,
    portrait: kidsBook5Vertical,
  },
} as const;

const ADULT_IMAGE_TEMPLATE_SOURCES = {
  EU1: {
    landscape: adultsHorizontal,
    portrait: adultsVertical,
  },
  EU2: {
    landscape: adultsHorizontal,
    portrait: adultsVertical,
  },
  EU3: {
    landscape: adultsHorizontal,
    portrait: adultsVertical,
  },
  EU4: {
    landscape: adultsHorizontal,
    portrait: adultsVertical,
  },
  EU5: {
    landscape: adultsHorizontal,
    portrait: adultsVertical,
  },
  EU6: {
    landscape: adultsHorizontal,
    portrait: adultsVertical,
  },
  EU7: {
    landscape: adultsHorizontal,
    portrait: adultsVertical,
  },
} as const;

const getKidsImageTemplateSource = (data: CertificateData | null) => {
  if (!data) {
    return null;
  }

  if (data.flow === "kids") {
    return (
      KIDS_IMAGE_TEMPLATE_SOURCES[
        data.book.code as keyof typeof KIDS_IMAGE_TEMPLATE_SOURCES
      ]?.[data.orientation] ?? null
    );
  }

  return (
    ADULT_IMAGE_TEMPLATE_SOURCES[
      data.book.code as keyof typeof ADULT_IMAGE_TEMPLATE_SOURCES
    ]?.[data.orientation] ?? null
  );
};

const getKidsImageTemplateDimensions = (orientation: CertificateOrientation) =>
  orientation === "portrait"
    ? {
        width: CD_PORT_W,
        height: CD_PORT_H,
      }
    : {
        width: CD_W,
        height: CD_H,
      };

const shouldUseKidsImageTemplate = (data: CertificateData | null) =>
  Boolean(getKidsImageTemplateSource(data));

const getMagicVillageTemplateData = (
  data: CertificateData,
  item: Pick<ReviewItem, "firstName" | "surname">,
): CertificateImageData => {
  const studentName = `${normalizeSpaces(item.firstName)} ${normalizeSpaces(
    item.surname,
  )}`.trim();

  return {
    flow: data.flow,
    studentName,
    completion: "ukończył/a",
    courseDuration: "",
    level: data.book.level,
    levelTitle:
      data.flow === "kids" ? (kidsBookTitles[data.book.sortOrder] ?? "") : "",
    bookTitle: normalizeSpaces(`English Unlocked ${data.book.sortOrder || ""}`),
    schoolName: data.schoolName,
    schoolLogoSrc: data.schoolLogoDataUrl,
    publisherLogoSrc: data.directPressLogoDataUrl,
    city: data.place,
    date: formatPolishDate(data.issueDate),
    signerName: getIssuerName(data),
    signerRole: "",
    palette: MAGIC_VILLAGE_PALETTE,
    accentColors: getCertificateAccentColors(data.book.code),
  };
};

const svgText = (value: string) => escapeSvgText(value);

const buildMagicVillageWavePaths = (
  width: number,
  height: number,
  gradientId = "waveFade",
) => {
  const innerW = width - CD_MARGIN * 2;
  const innerH = height - CD_MARGIN * 2;
  const padTop = 30;
  const padBottom = 30;
  const lineCount = Math.max(
    20,
    Math.round((innerH - padTop - padBottom) / 17) + 1,
  );
  const spacing = (innerH - padTop - padBottom) / (lineCount - 1);
  const segments = 10;
  const segW = innerW / segments;
  const paths: string[] = [];

  for (let i = 0; i < lineCount; i += 1) {
    const y = padTop + i * spacing;
    const amp = 4 + Math.sin(i * 0.45) * 1.6;
    const phaseShift = i * 0.5;
    let d = `M 0 ${y.toFixed(2)}`;

    for (let s = 0; s < segments; s += 1) {
      const x1 = s * segW;
      const x2 = (s + 1) * segW;
      const cx1 = x1 + segW * 0.25;
      const cx2 = x1 + segW * 0.75;
      const sign = (s + Math.floor(phaseShift)) % 2 === 0 ? 1 : -1;
      const dy = sign * amp;
      d += ` C ${cx1.toFixed(2)} ${(y + dy).toFixed(2)}, ${cx2.toFixed(2)} ${(y + dy).toFixed(2)}, ${x2.toFixed(2)} ${y.toFixed(2)}`;
    }

    paths.push(
      `<path d="${d}" fill="none" stroke="url(#${gradientId})" stroke-width="0.5" stroke-linecap="round"/>`,
    );
  }

  return paths.join("");
};

const buildMagicVillageHorizontalSvg = async (
  data: CertificateData,
  item: ReviewItem,
) => {
  const templateData = getMagicVillageTemplateData(data, item);
  const isPortrait = data.orientation === "portrait";
  const dimensions = getKidsImageTemplateDimensions(data.orientation);
  const pageWidth = dimensions.width;
  const pageHeight = dimensions.height;
  const innerWidth = pageWidth - CD_MARGIN * 2;
  const innerHeight = pageHeight - CD_MARGIN * 2;
  const titleY = isPortrait ? 250 : 250;
  const titleFontSize = isPortrait ? 112 : 128;
  const titleSubtitleY = isPortrait ? 280 : 280;
  const titleSubtitleWidth = 430;
  const firstDividerY = isPortrait ? 296 : 296;
  const studentIntroY = isPortrait ? 352 : 352;
  const studentNameCenterY = isPortrait ? 415 : 415;
  const paragraphY = isPortrait ? 478 : 478;
  const secondDividerY = isPortrait ? 536 : 536;
  const levelY = isPortrait ? 592 : 592;
  const signatureTextY = isPortrait ? 1110 : 790;
  const dateY = isPortrait ? 1154 : 790;
  const publisherLogoRightX = 166;
  const publisherLogoY = 54;
  const publisherLogoW = 92;
  const publisherLogoH = 68;
  const publisherLogoX = publisherLogoRightX - publisherLogoW;
  const schoolLogoX = 70;
  const schoolLogoY = isPortrait ? 1016 : 708;
  const schoolLogoH = 56;
  const dateX = isPortrait ? 70 : 726;
  const textWidth = isPortrait ? 470 : 600;
  const courseLevel = templateData.level;
  const certificateBookTitleParts = getMagicVillageTitleParts(
    templateData.bookTitle,
    templateData.levelTitle,
  );
  const templateImage =
    getKidsImageTemplateSource(data) ?? magicVillageHorizontal;
  const background = await urlToImageAssets(templateImage);
  const embeddedFontCss = await buildEmbeddedCertificateFontCss().catch(
    (error) => {
      console.warn("Nie udało się osadzić fontów w SVG:", error);
      return "";
    },
  );
  const publisherLogo = data.directPressLogoDataUrl ?? "";
  const schoolLogo = data.schoolLogoDataUrl ?? "";
  const accentColors = templateData.accentColors;
  const useSingleDividerColor = data.flow === "adults";
  const firstDividerColors = useSingleDividerColor
    ? [
        ADULT_CERTIFICATE_DIVIDER_COLORS[0],
        ADULT_CERTIFICATE_DIVIDER_COLORS[1],
        ADULT_CERTIFICATE_DIVIDER_COLORS[2],
      ]
    : [
        accentColors.barSecondary,
        accentColors.barPrimary,
        accentColors.barDark,
      ];
  const secondDividerColors = useSingleDividerColor
    ? [
        ADULT_CERTIFICATE_DIVIDER_COLORS[2],
        ADULT_CERTIFICATE_DIVIDER_COLORS[0],
        ADULT_CERTIFICATE_DIVIDER_COLORS[1],
      ]
    : [
        accentColors.barDark,
        accentColors.barSecondary,
        accentColors.barPrimary,
      ];
  const studentNameMaxWidth = textWidth - 20;
  const studentNameFontSize = getResponsiveStudentNameFontSize(
    templateData.studentName,
    studentNameMaxWidth,
  );
  const studentNameFitAttributes = shouldCondenseStudentName(
    templateData.studentName,
    studentNameFontSize,
    studentNameMaxWidth,
  )
    ? ` textLength="${studentNameMaxWidth}" lengthAdjust="spacingAndGlyphs"`
    : "";

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg width="${pageWidth}" height="${pageHeight}" viewBox="0 0 ${pageWidth} ${pageHeight}" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <style><![CDATA[
      ${embeddedFontCss}
      text { text-rendering: geometricPrecision; }
    ]]></style>
    <clipPath id="innerClip">
      <rect x="${CD_MARGIN}" y="${CD_MARGIN}" width="${innerWidth}" height="${innerHeight}" rx="24" ry="24"/>
    </clipPath>
    <linearGradient id="waveFade" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="#000000" stop-opacity="0.18"/>
      <stop offset="100%" stop-color="#000000" stop-opacity="0.18"/>
    </linearGradient>
  </defs>
  <rect width="${pageWidth}" height="${pageHeight}" fill="#ffffff"/>
  <g clip-path="url(#innerClip)">
    <image href="${background.dataUrl}" x="${CD_MARGIN}" y="${CD_MARGIN}" width="${innerWidth}" height="${innerHeight}" preserveAspectRatio="xMidYMid slice"/>
  </g>
  <g transform="translate(${CD_MARGIN} ${CD_MARGIN})">
    ${buildMagicVillageWavePaths(pageWidth, pageHeight)}
  </g>
  <rect x="${CD_MARGIN}" y="${CD_MARGIN}" width="${innerWidth}" height="${innerHeight}" rx="24" ry="24" fill="none" stroke="#15140f" stroke-opacity="0.14"/>

  ${
    schoolLogo
      ? `<image href="${schoolLogo}" x="${schoolLogoX}" y="${schoolLogoY}" width="360" height="${schoolLogoH}" preserveAspectRatio="xMinYMid meet"/>`
      : `<text x="${schoolLogoX}" y="${schoolLogoY + 30}" font-family="DM Sans, Arial, sans-serif" font-size="14" font-weight="700" fill="#64748b">LOGO SZKOŁY</text>`
  }

  ${
    publisherLogo
      ? `<image href="${publisherLogo}" x="${publisherLogoX}" y="${publisherLogoY}" width="${publisherLogoW}" height="${publisherLogoH}" preserveAspectRatio="xMidYMid meet"/>`
      : `<rect x="${publisherLogoX}" y="${publisherLogoY}" width="${publisherLogoW}" height="${publisherLogoH}" rx="8" fill="none" stroke="#15140f" stroke-opacity="0.4" stroke-dasharray="4 4"/>`
  }

  <text x="60" y="${titleY}" font-family="Fira Sans, Arial, sans-serif" font-size="${titleFontSize}" font-weight="500" letter-spacing="-1" fill="#15140f">Certificate</text>
  <text x="72" y="${titleSubtitleY}" font-family="DM Sans, Arial, sans-serif" font-size="18" font-weight="700" letter-spacing="2.4" fill="#6b7280" textLength="${titleSubtitleWidth}" lengthAdjust="spacing">OF COURSE COMPLETION</text>

  <rect x="70" y="${firstDividerY}" width="32" height="12" rx="6" fill="${firstDividerColors[0]}"/>
  <rect x="116" y="${firstDividerY}" width="48" height="12" rx="6" fill="${firstDividerColors[1]}"/>
  <rect x="178" y="${firstDividerY}" width="70" height="12" rx="6" fill="${firstDividerColors[2]}"/>

  <text x="70" y="${studentIntroY}" font-family="DM Sans, Arial, sans-serif" font-size="18" fill="rgba(0,0,0,.78)">This certificate confirms that</text>
  <text x="70" y="${studentNameCenterY}" dominant-baseline="middle" font-family="Bricolage Grotesque, Arial, sans-serif" font-size="${studentNameFontSize}" font-weight="500" letter-spacing="-1" fill="#15140f"${studentNameFitAttributes}>${svgText(templateData.studentName)}</text>

  <text x="70" y="${paragraphY}" font-family="DM Sans, Arial, sans-serif" font-size="18" fill="rgba(0,0,0,.78)">
    <tspan x="70" dy="0">${svgText(`has completed an English language course at level ${courseLevel},`)}</tspan>
    <tspan x="70" dy="30">based on the </tspan><tspan font-weight="700" fill="#15140f">${svgText(certificateBookTitleParts.prefix)}</tspan><tspan> </tspan><tspan font-family="Bricolage Grotesque, Arial, sans-serif" font-size="20" font-style="italic" font-weight="500" fill="${accentColors.subtitle}">${svgText(certificateBookTitleParts.magicVillage)}</tspan><tspan>.</tspan>
  </text>

  <rect x="70" y="${secondDividerY}" width="70" height="12" rx="6" fill="${secondDividerColors[0]}"/>
  <rect x="154" y="${secondDividerY}" width="32" height="12" rx="6" fill="${secondDividerColors[1]}"/>
  <rect x="200" y="${secondDividerY}" width="48" height="12" rx="6" fill="${secondDividerColors[2]}"/>

  <text x="70" y="${levelY}" font-family="Bricolage Grotesque, Arial, sans-serif" font-size="38" font-weight="700" fill="${accentColors.level}">${svgText(templateData.level)}</text>

  <text x="70" y="${signatureTextY}" font-family="Caveat, cursive" font-size="30" font-weight="600" fill="#15140f">${svgText(templateData.signerName)}</text>
  <text x="${dateX}" y="${dateY}" text-anchor="${isPortrait ? "start" : "end"}" font-family="DM Sans, Arial, sans-serif" font-size="14" font-weight="500" fill="rgba(0,0,0,.85)">${svgText(`${templateData.city}, ${templateData.date}`)}</text>
</svg>`;
};

const renderMagicVillageHorizontalToPngBlob = async (
  data: CertificateData,
  item: ReviewItem,
) => {
  const svg = await buildMagicVillageHorizontalSvg(data, item);
  const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("magic-village-svg-render-failed"));
      img.src = svgUrl;
    });
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const dimensions = getKidsImageTemplateDimensions(data.orientation);

    if (!context) {
      throw new Error("magic-village-canvas-unavailable");
    }

    canvas.width = dimensions.width * MAGIC_VILLAGE_EXPORT_SCALE;
    canvas.height = dimensions.height * MAGIC_VILLAGE_EXPORT_SCALE;
    context.scale(MAGIC_VILLAGE_EXPORT_SCALE, MAGIC_VILLAGE_EXPORT_SCALE);
    context.drawImage(image, 0, 0, dimensions.width, dimensions.height);

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error("magic-village-png-export-failed"));
      }, "image/png");
    });
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
};

const CdPillDivider: React.FC<{
  palette: string[];
  accentColors?: CertificateAccentColors;
  flow?: CertificateFlow;
  shuffled?: boolean;
}> = ({ palette, accentColors, flow = "kids", shuffled = false }) => {
  const [c1, c2, , c4] = palette;
  const barPrimary = accentColors?.barPrimary ?? c1;
  const barSecondary = accentColors?.barSecondary ?? c2;
  const barDark = accentColors?.barDark ?? c4;
  const useSingleDividerColor = flow === "adults";
  const pills = shuffled
    ? useSingleDividerColor
      ? [
          { w: 32, color: ADULT_CERTIFICATE_DIVIDER_COLORS[0] },
          { w: 48, color: ADULT_CERTIFICATE_DIVIDER_COLORS[1] },
          { w: 70, color: ADULT_CERTIFICATE_DIVIDER_COLORS[2] },
        ]
      : [
          { w: 32, color: barSecondary },
          { w: 48, color: barPrimary },
          { w: 70, color: barDark },
        ]
    : useSingleDividerColor
      ? [
          { w: 70, color: ADULT_CERTIFICATE_DIVIDER_COLORS[2] },
          { w: 32, color: ADULT_CERTIFICATE_DIVIDER_COLORS[0] },
          { w: 48, color: ADULT_CERTIFICATE_DIVIDER_COLORS[1] },
        ]
      : [
          { w: 70, color: barDark },
          { w: 32, color: barSecondary },
          { w: 48, color: barPrimary },
        ];

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      {pills.map((pill, index) => (
        <div
          key={index}
          style={{
            width: pill.w,
            height: 12,
            borderRadius: 6,
            background: pill.color,
          }}
        />
      ))}
    </div>
  );
};

const CdLogoSchool: React.FC<{
  logoSrc: string | null;
}> = ({ logoSrc }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
    <div
      style={{
        width: 300,
        height: 44,
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
      }}
    >
      {logoSrc ? (
        <img
          src={logoSrc}
          alt=""
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      ) : (
        <span
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 8,
            color: "rgba(0,0,0,.55)",
            fontWeight: 700,
            textAlign: "center",
            lineHeight: 1.1,
          }}
        >
          SZKOŁA
        </span>
      )}
    </div>
  </div>
);

const CdPublisherBlock: React.FC<{
  ink: string;
  logoSrc: string | null;
  align?: "left" | "right";
}> = ({ ink, logoSrc, align = "right" }) => {
  const isLeft = align === "left";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexDirection: isLeft ? "row" : "row-reverse",
      }}
    >
      <div
        style={{
          width: 54,
          height: 44,
          display: "grid",
          placeItems: "center",
        }}
      >
        {logoSrc ? (
          <img
            src={logoSrc}
            alt=""
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        ) : (
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 8,
              border: `1.5px dashed ${ink}66`,
            }}
          />
        )}
      </div>
    </div>
  );
};

const CdSignature: React.FC<{
  name: string;
  role: string;
  ink: string;
}> = ({ name }) => (
  <div style={{ width: 260 }}>
    <div
      style={{
        fontFamily: "'Caveat', cursive",
        fontSize: 30,
        fontWeight: 600,
        lineHeight: 1,
        color: "#15140f",
        minHeight: 38,
      }}
    >
      {name}
    </div>
  </div>
);

const CdDateBlock: React.FC<{
  city: string;
  date: string;
  align?: "left" | "center" | "right";
}> = ({ city, date, align = "center" }) => (
  <div style={{ textAlign: align }}>
    <div
      style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 14,
        fontWeight: 500,
        color: "rgba(0,0,0,.85)",
      }}
    >
      {city}, {date}
    </div>
  </div>
);

const PremiumBackground: React.FC<{
  w: number;
  h: number;
  margin?: number;
  fadeStart?: number;
  fadeEnd?: number;
}> = ({ w, h, margin = CD_MARGIN, fadeStart = 55, fadeEnd = 80 }) => {
  const innerW = w - margin * 2;
  const innerH = h - margin * 2;
  const padTop = 30;
  const padBottom = 30;
  const lineCount = Math.max(
    20,
    Math.round((innerH - padTop - padBottom) / 17) + 1,
  );
  const spacing = (innerH - padTop - padBottom) / (lineCount - 1);
  const segments = 10;
  const segW = innerW / segments;
  const lines = [];

  for (let i = 0; i < lineCount; i += 1) {
    const y = padTop + i * spacing;
    const amp = 4 + Math.sin(i * 0.45) * 1.6;
    const phaseShift = i * 0.5;
    let d = `M 0 ${y.toFixed(2)}`;

    for (let s = 0; s < segments; s += 1) {
      const x1 = s * segW;
      const x2 = (s + 1) * segW;
      const cx1 = x1 + segW * 0.25;
      const cx2 = x1 + segW * 0.75;
      const sign = (s + Math.floor(phaseShift)) % 2 === 0 ? 1 : -1;
      const dy = sign * amp;
      d += ` C ${cx1.toFixed(2)} ${(y + dy).toFixed(2)}, ${cx2.toFixed(2)} ${(y + dy).toFixed(2)}, ${x2.toFixed(2)} ${y.toFixed(2)}`;
    }

    lines.push(
      <path
        key={i}
        d={d}
        fill="none"
        strokeWidth="0.5"
        strokeLinecap="round"
      />,
    );
  }

  const maskId = `wave-fade-${w}-${h}-${fadeStart}-${fadeEnd}`;

  return (
    <div
      style={{
        position: "absolute",
        top: margin,
        left: margin,
        width: innerW,
        height: innerH,
        borderRadius: 24,
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      <svg
        width={innerW}
        height={innerH}
        viewBox={`0 0 ${innerW} ${innerH}`}
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id={maskId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#000000" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0.18" />
          </linearGradient>
        </defs>
        <g stroke={`url(#${maskId})`}>{lines}</g>
      </svg>
    </div>
  );
};

const CertificateImageLandscape: React.FC<{
  data: CertificateImageData;
  imageSrc: string;
}> = ({ data, imageSrc }) => {
  const {
    studentName,
    level,
    levelTitle,
    bookTitle,
    schoolLogoSrc,
    publisherLogoSrc,
    city,
    date,
    signerName,
    signerRole,
    palette,
    accentColors,
  } = data;
  const ink = palette[3];
  const primary = accentColors.level;
  const cefr = level;
  const certificateBookTitleParts = getMagicVillageTitleParts(
    bookTitle,
    levelTitle,
  );
  const studentNameMaxWidth = 580;
  const studentNameFontSize = getResponsiveStudentNameFontSize(
    studentName,
    studentNameMaxWidth,
  );

  return (
    <div
      style={{
        width: CD_W,
        height: CD_H,
        background: "#ffffff",
        position: "relative",
        overflow: "hidden",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        color: "#15140f",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: CD_MARGIN,
          borderRadius: 24,
          overflow: "hidden",
        }}
      >
        <img
          src={imageSrc}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "right center",
            display: "block",
          }}
        />
      </div>
      <PremiumBackground w={CD_W} h={CD_H} fadeStart={42} fadeEnd={68} />
      <div
        style={{
          position: "absolute",
          inset: CD_MARGIN,
          border: `1px solid ${ink}22`,
          borderRadius: 24,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "absolute",
          top: 56,
          left: 70,
          width: 660,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <CdPublisherBlock ink={ink} logoSrc={publisherLogoSrc} align="left" />
      </div>

      <div
        style={{
          position: "absolute",
          left: 60,
          top: 140,
          fontFamily: "'Fira Sans', sans-serif",
          fontSize: 128,
          fontWeight: 500,
          letterSpacing: "-.008em",
          lineHeight: 0.92,
          color: "#15140f",
        }}
      >
        Certificate
      </div>
      <div
        style={{
          position: "absolute",
          left: 72,
          top: 254,
          width: 430,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: 2.4,
          color: "#6b7280",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        OF COURSE COMPLETION
      </div>

      <div style={{ position: "absolute", left: 70, top: 296 }}>
        <CdPillDivider
          palette={palette}
          accentColors={accentColors}
          flow={data.flow}
          shuffled
        />
      </div>

      <div
        style={{
          position: "absolute",
          left: 70,
          top: 345,
          width: 600,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 18,
          lineHeight: 1.55,
          color: "rgba(0,0,0,.78)",
        }}
      >
        This certificate confirms that
      </div>

      <div
        style={{
          position: "absolute",
          left: 70,
          top: 373,
          width: 600,
          height: 77,
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: studentNameFontSize,
            fontWeight: 500,
            letterSpacing: "-.015em",
            lineHeight: 1.05,
            color: "#15140f",
            maxWidth: studentNameMaxWidth,
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {studentName}
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          left: 70,
          top: 450,
          width: 600,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 18,
          lineHeight: 1.55,
          color: "rgba(0,0,0,.78)",
        }}
      >
        has completed an English language course at level {cefr},
        <br />
        based on the{" "}
        <span style={{ fontWeight: 700, color: ink }}>
          {certificateBookTitleParts.prefix}
        </span>
        <span> </span>
        <span
          style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 20,
            fontStyle: "italic",
            fontWeight: 500,
            color: accentColors.subtitle,
          }}
        >
          {certificateBookTitleParts.magicVillage}
        </span>
        .
      </div>

      <div style={{ position: "absolute", left: 70, top: 536 }}>
        <CdPillDivider
          palette={palette}
          accentColors={accentColors}
          flow={data.flow}
        />
      </div>

      <div style={{ position: "absolute", left: 70, top: 574 }}>
        <div
          style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 38,
            fontWeight: 600,
            letterSpacing: "-.01em",
            color: primary,
            lineHeight: 1,
          }}
        >
          {cefr}
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          left: 70,
          bottom: 30,
          display: "flex",
          alignItems: "flex-end",
          gap: 130,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          <CdLogoSchool logoSrc={schoolLogoSrc} />
          <CdSignature name={signerName} role={signerRole} ink={ink} />
        </div>
        <div style={{ paddingBottom: 4 }}>
          <CdDateBlock city={city} date={date} align="left" />
        </div>
      </div>
    </div>
  );
};

const CertificateImagePortrait: React.FC<{
  data: CertificateImageData;
  imageSrc: string;
}> = ({ data, imageSrc }) => {
  const {
    studentName,
    level,
    levelTitle,
    bookTitle,
    schoolLogoSrc,
    publisherLogoSrc,
    city,
    date,
    signerName,
    signerRole,
    palette,
    accentColors,
  } = data;
  const ink = palette[3];
  const primary = accentColors.level;
  const cefr = level;
  const certificateBookTitleParts = getMagicVillageTitleParts(
    bookTitle,
    levelTitle,
  );
  const studentNameMaxWidth = 500;
  const studentNameFontSize = getResponsiveStudentNameFontSize(
    studentName,
    studentNameMaxWidth,
  );

  return (
    <div
      style={{
        width: CD_PORT_W,
        height: CD_PORT_H,
        background: "#ffffff",
        position: "relative",
        overflow: "hidden",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        color: "#15140f",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: CD_MARGIN,
          borderRadius: 24,
          overflow: "hidden",
        }}
      >
        <img
          src={imageSrc}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "right center",
            display: "block",
          }}
        />
      </div>
      <PremiumBackground
        w={CD_PORT_W}
        h={CD_PORT_H}
        fadeStart={46}
        fadeEnd={74}
      />
      <div
        style={{
          position: "absolute",
          inset: CD_MARGIN,
          border: `1px solid ${ink}22`,
          borderRadius: 24,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "absolute",
          top: 56,
          left: 70,
          width: 590,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <CdPublisherBlock ink={ink} logoSrc={publisherLogoSrc} align="left" />
      </div>

      <div
        style={{
          position: "absolute",
          left: 60,
          top: 140,
          fontFamily: "'Fira Sans', sans-serif",
          fontSize: 112,
          fontWeight: 500,
          letterSpacing: "-.008em",
          lineHeight: 0.92,
          color: "#15140f",
        }}
      >
        Certificate
      </div>
      <div
        style={{
          position: "absolute",
          left: 72,
          top: 254,
          width: 430,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: 2.4,
          color: "#6b7280",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        OF COURSE COMPLETION
      </div>

      <div style={{ position: "absolute", left: 70, top: 296 }}>
        <CdPillDivider
          palette={palette}
          accentColors={accentColors}
          flow={data.flow}
          shuffled
        />
      </div>

      <div
        style={{
          position: "absolute",
          left: 70,
          top: 345,
          width: 470,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 18,
          lineHeight: 1.55,
          color: "rgba(0,0,0,.78)",
        }}
      >
        This certificate confirms that
      </div>

      <div
        style={{
          position: "absolute",
          left: 70,
          top: 373,
          width: 470,
          height: 77,
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: studentNameFontSize,
            fontWeight: 500,
            letterSpacing: "-.015em",
            lineHeight: 1.05,
            color: "#15140f",
            maxWidth: studentNameMaxWidth,
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {studentName}
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          left: 70,
          top: 450,
          width: 470,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 18,
          lineHeight: 1.55,
          color: "rgba(0,0,0,.78)",
        }}
      >
        has completed an English language course at level {cefr},
        <br />
        based on the{" "}
        <span style={{ fontWeight: 700, color: ink }}>
          {certificateBookTitleParts.prefix}
        </span>
        <span> </span>
        <span
          style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 20,
            fontStyle: "italic",
            fontWeight: 500,
            color: accentColors.subtitle,
          }}
        >
          {certificateBookTitleParts.magicVillage}
        </span>
        .
      </div>

      <div style={{ position: "absolute", left: 70, top: 536 }}>
        <CdPillDivider
          palette={palette}
          accentColors={accentColors}
          flow={data.flow}
        />
      </div>

      <div style={{ position: "absolute", left: 70, top: 574 }}>
        <div
          style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 38,
            fontWeight: 600,
            letterSpacing: "-.01em",
            color: primary,
            lineHeight: 1,
          }}
        >
          {cefr}
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          left: 70,
          bottom: 106,
        }}
      >
        <CdLogoSchool logoSrc={schoolLogoSrc} />
        <div style={{ marginTop: 24 }}>
          <CdSignature name={signerName} role={signerRole} ink={ink} />
        </div>
        <div style={{ marginTop: 24 }}>
          <CdDateBlock city={city} date={date} align="left" />
        </div>
      </div>
    </div>
  );
};

const ScaledMagicVillagePreview: React.FC<{
  templateData: CertificateImageData;
  imageSrc: string;
  orientation: CertificateOrientation;
}> = ({ templateData, imageSrc, orientation }) => {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0.5);
  const dimensions = getKidsImageTemplateDimensions(orientation);

  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;

    const updateScale = () => {
      const widthScale = outer.clientWidth / dimensions.width;
      const heightScale = outer.clientHeight / dimensions.height;
      setScale(Math.min(widthScale, heightScale));
    };

    updateScale();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateScale);
      return () => window.removeEventListener("resize", updateScale);
    }

    const observer = new ResizeObserver(updateScale);
    observer.observe(outer);

    return () => observer.disconnect();
  }, [dimensions.height, dimensions.width]);

  return (
    <div
      ref={outerRef}
      className={`w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-inner ${
        orientation === "portrait"
          ? "mx-auto aspect-[849/1200] max-h-[760px]"
          : "aspect-[1200/849]"
      }`}
    >
      <div
        style={{
          width: dimensions.width,
          height: dimensions.height,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        {orientation === "portrait" ? (
          <CertificateImagePortrait data={templateData} imageSrc={imageSrc} />
        ) : (
          <CertificateImageLandscape data={templateData} imageSrc={imageSrc} />
        )}
      </div>
    </div>
  );
};

const addPlaceCandidate = (candidates: string[], value: unknown) => {
  if (typeof value !== "string") return;

  const candidate = toPlaceTitleCase(normalizeSpaces(value));
  if (!candidate) return;

  const normalizedCandidate = candidate.toLocaleLowerCase("pl-PL");
  const alreadyExists = candidates.some(
    (item) => item.toLocaleLowerCase("pl-PL") === normalizedCandidate,
  );

  if (!alreadyExists) {
    candidates.push(candidate);
  }
};

const toPlaceTitleCase = (value: string) =>
  value
    .toLocaleLowerCase("pl-PL")
    .split(/(\s|-)/)
    .map((part) =>
      part.trim() && part !== "-"
        ? `${part.charAt(0).toLocaleUpperCase("pl-PL")}${part.slice(1)}`
        : part,
    )
    .join("");

const getSchoolPlaceCandidates = (school: School | null) => {
  const candidates: string[] = [];
  const schoolData = school as any;

  addPlaceCandidate(candidates, schoolData?.city);
  addPlaceCandidate(candidates, schoolData?.miasto);

  [
    schoolData?.addresses,
    schoolData?.adresy,
    schoolData?.locations,
    schoolData?.branches,
  ].forEach((items) => {
    if (!Array.isArray(items)) return;

    items.forEach((item) => {
      if (typeof item === "string") {
        addPlaceCandidate(candidates, item);
        return;
      }

      addPlaceCandidate(candidates, item?.city);
      addPlaceCandidate(candidates, item?.miasto);
      addPlaceCandidate(candidates, item?.address?.city);
      addPlaceCandidate(candidates, item?.adres?.miasto);
    });
  });

  return candidates;
};

const buildPdfDefinition = (data: CertificateData, item: ReviewItem) => {
  const flowConfig = getFlowConfig(data.flow);
  const issuerName = getIssuerName(data);
  const studentName = `${normalizeSpaces(item.firstName)} ${normalizeSpaces(
    item.surname,
  )}`.trim();
  const images: Record<string, string> = {};

  if (data.directPressLogoDataUrl && !data.directPressLogoSvg) {
    images.directPressLogo = data.directPressLogoDataUrl;
  }

  if (data.schoolLogoDataUrl) {
    images.schoolLogo = data.schoolLogoDataUrl;
  }

  const logoColumns: any[] = [
    data.schoolLogoDataUrl
      ? {
          image: "schoolLogo",
          fit: [150, 80],
          alignment: "left",
        }
      : {
          svg: buildSchoolNameLogoSvg(data.schoolName),
          fit: [150, 80],
          alignment: "left",
        },
    data.directPressLogoSvg
      ? {
          svg: data.directPressLogoSvg,
          fit: [142, 52],
          alignment: "right",
          margin: [0, 10, 0, 0],
        }
      : data.directPressLogoDataUrl
        ? {
            image: "directPressLogo",
            fit: [142, 52],
            alignment: "right",
            margin: [0, 10, 0, 0],
          }
        : {
            text: "DIRECT PRESS",
            alignment: "right",
            bold: true,
            color: "#0f172a",
            margin: [0, 22, 0, 0],
          },
  ];

  return {
    pageSize: "A4",
    pageOrientation: data.orientation,
    pageMargins: [48, 34, 48, 34],
    images,
    background: [
      {
        canvas: [
          {
            type: "rect",
            x: 22,
            y: 22,
            w: data.orientation === "landscape" ? 798 : 552,
            h: data.orientation === "landscape" ? 552 : 798,
            r: 20,
            lineWidth: 1.4,
            lineColor: "#f59e0b",
          },
          {
            type: "rect",
            x: 34,
            y: 34,
            w: data.orientation === "landscape" ? 774 : 528,
            h: data.orientation === "landscape" ? 528 : 774,
            r: 16,
            lineWidth: 0.7,
            lineColor: "#cbd5e1",
          },
        ],
      },
    ],
    content: [
      {
        columns: logoColumns,
        columnGap: 24,
        margin: [0, 0, 0, 26],
      },
      {
        text: flowConfig.documentTitle,
        alignment: "center",
        fontSize: 34,
        bold: true,
        color: "#0f172a",
        margin: [0, 0, 0, 16],
      },
      {
        text: studentName,
        alignment: "center",
        fontSize: 42,
        bold: true,
        color: "#9a5b1a",
        margin: [0, 0, 0, 22],
      },
      {
        table: {
          widths: ["20%", "30%", "20%", "30%"],
          body: [
            [
              { text: "Szkoła", style: "metaLabel" },
              { text: data.schoolName, style: "metaValue" },
              { text: "Poziom", style: "metaLabel" },
              { text: data.book.level, style: "metaValue" },
            ],
            [
              { text: "Książka", style: "metaLabel" },
              { text: data.book.title, style: "metaValue", colSpan: 3 },
              {},
              {},
            ],
            [
              { text: "Data", style: "metaLabel" },
              { text: formatPolishDate(data.issueDate), style: "metaValue" },
              { text: "Miejsce", style: "metaLabel" },
              { text: data.place, style: "metaValue" },
            ],
            [
              { text: "Osoba podpisująca", style: "metaLabel" },
              { text: issuerName, style: "metaValue", colSpan: 3 },
              {},
              {},
            ],
          ],
        },
        layout: {
          hLineColor: () => "#e2e8f0",
          vLineColor: () => "#e2e8f0",
          paddingLeft: () => 10,
          paddingRight: () => 10,
          paddingTop: () => 8,
          paddingBottom: () => 8,
        },
        margin: [50, 0, 50, 42],
      },
      {
        columns: [
          {
            width: "*",
            text: "",
          },
          {
            width: 240,
            stack: [
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 0,
                    x2: 240,
                    y2: 0,
                    lineWidth: 1,
                    lineColor: "#334155",
                  },
                ],
                margin: [0, 0, 0, 8],
              },
              {
                text: issuerName,
                alignment: "center",
                fontSize: 11,
                color: "#0f172a",
              },
            ],
          },
        ],
      },
    ],
    styles: {
      metaLabel: {
        bold: true,
        fontSize: 9,
        color: "#475569",
      },
      metaValue: {
        fontSize: 10,
        color: "#0f172a",
      },
    },
    defaultStyle: {
      font: "Roboto",
    },
  };
};

const buildPdfBlob = async (data: CertificateData, item: ReviewItem) => {
  return new Promise<Blob>((resolve, reject) => {
    try {
      (pdfMake as any)
        .createPdf(buildPdfDefinition(data, item))
        .getBlob((blob: Blob) => {
          resolve(blob);
        });
    } catch (error) {
      reject(error);
    }
  });
};

const getGeneratedDocumentFormat = (data: CertificateData) =>
  shouldUseKidsImageTemplate(data) ? "png" : "pdf";

const buildGeneratedDocumentBlob = (data: CertificateData, item: ReviewItem) =>
  getGeneratedDocumentFormat(data) === "png"
    ? renderMagicVillageHorizontalToPngBlob(data, item)
    : buildPdfBlob(data, item);

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const buildPdfFilename = (data: CertificateData, item: ReviewItem) =>
  `${sanitizeFilenamePart(item.surname)}_${getOrientationFilenameCode(
    data.orientation,
  )}_${getIssueYear(data.issueDate)}.pdf`;

const buildPngFilename = (data: CertificateData, item: ReviewItem) =>
  `${sanitizeFilenamePart(item.surname)}_${getOrientationFilenameCode(
    data.orientation,
  )}_${getIssueYear(data.issueDate)}.png`;

const buildGeneratedDocumentFilename = (
  data: CertificateData,
  item: ReviewItem,
) =>
  getGeneratedDocumentFormat(data) === "png"
    ? buildPngFilename(data, item)
    : buildPdfFilename(data, item);

const buildZipFilename = (data: CertificateData, count: number) => {
  const flowConfig = getFlowConfig(data.flow);
  return `${getIssueYear(data.issueDate)}_${sanitizeFilenamePart(
    data.book.code,
  )}_${getOrientationFilenameCode(data.orientation)}_${count}-${flowConfig.zipSuffix}.zip`;
};

const CertificatePreview: React.FC<{
  data: CertificateData | null;
  flow: CertificateFlow;
  previewStudent?: Pick<ReviewItem, "firstName" | "surname">;
  pdfPreviewUrl?: string | null;
  showInitialPreview?: boolean;
}> = ({
  data,
  flow,
  previewStudent,
  pdfPreviewUrl = null,
  showInitialPreview = true,
}) => {
  const flowConfig = getFlowConfig(flow);
  const issuerName = data ? getIssuerName(data) : "";
  const kidsImageTemplateSource = getKidsImageTemplateSource(data);
  const magicVillagePreviewData =
    data && kidsImageTemplateSource
      ? getMagicVillageTemplateData(
          data,
          previewStudent ?? DEFAULT_PREVIEW_STUDENT,
        )
      : null;

  if (!showInitialPreview && !pdfPreviewUrl) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex min-h-[520px] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-sm font-semibold text-slate-500">
          Dodaj ucznia, by wygenerować certyfikat.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
          Podgląd
        </p>
      </div>

      {pdfPreviewUrl ? (
        <div
          className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-inner ${
            data?.orientation === "portrait"
              ? "mx-auto aspect-[0.707/1] max-h-[760px]"
              : "aspect-[1.414/1]"
          }`}
        >
          <iframe
            src={pdfPreviewUrl}
            title="Podgląd PDF"
            className="h-full w-full"
          />
        </div>
      ) : magicVillagePreviewData ? (
        <ScaledMagicVillagePreview
          templateData={magicVillagePreviewData}
          imageSrc={kidsImageTemplateSource ?? magicVillageHorizontal}
          orientation={data?.orientation ?? "landscape"}
        />
      ) : (
        <div
          className={`overflow-hidden rounded-2xl border border-slate-200 bg-[#fffaf2] p-[3.5%] shadow-inner ${
            data?.orientation === "portrait"
              ? "mx-auto aspect-[0.707/1] max-h-[760px]"
              : "aspect-[1.414/1]"
          }`}
        >
          <div className="flex h-full flex-col rounded-[20px] border border-amber-300/80 bg-white p-[3%]">
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-h-[64px] w-[26%] items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 py-2">
                {data?.schoolLogoDataUrl ? (
                  <img
                    src={data.schoolLogoDataUrl}
                    alt="Logo szkoły"
                    className="max-h-12 max-w-full object-contain"
                  />
                ) : (
                  <p className="line-clamp-2 text-center text-[10px] font-bold leading-tight text-slate-900">
                    {data?.schoolName || "Szkoła"}
                  </p>
                )}
              </div>
              {data?.directPressLogoDataUrl ? (
                <img
                  src={data.directPressLogoDataUrl}
                  alt="Direct Press"
                  className="mt-1 max-h-12 max-w-[24%] object-contain"
                />
              ) : (
                <p className="mt-3 text-sm font-bold text-slate-900">
                  DIRECT PRESS
                </p>
              )}
            </div>

            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <p className="text-4xl font-bold tracking-[-0.04em] text-slate-900 sm:text-5xl">
                {flowConfig.documentTitle}
              </p>
              <p className="mt-5 text-4xl font-bold tracking-[-0.04em] text-[#9a5b1a] sm:text-6xl">
                {DEFAULT_PREVIEW_STUDENT.firstName}{" "}
                {DEFAULT_PREVIEW_STUDENT.surname}
              </p>

              <div className="mt-7 grid w-full max-w-[82%] grid-cols-2 gap-3 text-left text-xs text-slate-600">
                <div>
                  <span className="font-bold text-slate-900">Szkoła: </span>
                  {data?.schoolName || "-"}
                </div>
                <div>
                  <span className="font-bold text-slate-900">Poziom: </span>
                  {data?.book.level || "-"}
                </div>
                <div>
                  <span className="font-bold text-slate-900">Data: </span>
                  {data?.issueDate ? formatPolishDate(data.issueDate) : "-"}
                </div>
                <div>
                  <span className="font-bold text-slate-900">Miejsce: </span>
                  {data?.place || "-"}
                </div>
                <div>
                  <span className="font-bold text-slate-900">Książka: </span>
                  {data?.book.title || "-"}
                </div>
              </div>
            </div>

            <div className="ml-auto w-56 border-t border-slate-500 pt-2 text-center text-xs text-slate-700">
              {issuerName || "-"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

type CertificateGeneratorProps = {
  selectorHighlightSignal?: number;
};

const CertificateGenerator: React.FC<CertificateGeneratorProps> = ({
  selectorHighlightSignal = 0,
}) => {
  const ctx = useCtx();
  const [flow, setFlow] = useState<CertificateFlow | null>(null);
  const [issueDate, setIssueDate] = useState(() =>
    formatDateInputValue(new Date()),
  );
  const [place, setPlace] = useState("");
  const [orientation, setOrientation] =
    useState<CertificateOrientation>("landscape");
  const [schoolName, setSchoolName] = useState("");
  const [selectedBookId, setSelectedBookId] = useState("");
  const [issuerName, setIssuerName] = useState("");
  const [studentRows, setStudentRows] = useState<StudentRow[]>([]);
  const [hasAddedStudentInSession, setHasAddedStudentInSession] =
    useState(false);
  const [newStudentRowId, setNewStudentRowId] = useState<string | null>(null);
  const [certificateStudents, setCertificateStudents] = useState<CertificateStudent[]>([]);
  const [isLoadingCertificateStudents, setIsLoadingCertificateStudents] =
    useState(false);
  const [savingStudentRowId, setSavingStudentRowId] = useState<string | null>(
    null,
  );
  const [directPressLogoDataUrl, setDirectPressLogoDataUrl] = useState<
    string | null
  >(null);
  const [directPressLogoSvg, setDirectPressLogoSvg] = useState<string | null>(
    null,
  );
  const [schoolLogoDataUrl, setSchoolLogoDataUrl] = useState<string | null>(
    null,
  );
  const [schoolLogoStatus, setSchoolLogoStatus] = useState<
    "idle" | "loading" | "ready" | "missing" | "failed"
  >("idle");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [message, setMessage] = useState<Message | null>(null);
  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([]);
  const [generationConfirmation, setGenerationConfirmation] = useState<{
    id: string;
    count: number;
  } | null>(null);
  const [reviewData, setReviewData] = useState<CertificateData | null>(null);
  const [isReviewVisible, setIsReviewVisible] = useState(false);
  const [previewingItemId, setPreviewingItemId] = useState<string | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [isStudentPanelOpen, setIsStudentPanelOpen] = useState(false);
  const [previewModalIndex, setPreviewModalIndex] = useState<number | null>(
    null,
  );
  const studentListRef = useRef<HTMLDivElement | null>(null);
  const selectorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!generationConfirmation) return;

    const timeoutId = window.setTimeout(() => {
      setGenerationConfirmation(null);
    }, 3200);

    return () => window.clearTimeout(timeoutId);
  }, [generationConfirmation]);

  useEffect(() => {
    if (!newStudentRowId) return;

    const timeoutId = window.setTimeout(() => {
      setNewStudentRowId(null);
    }, 850);

    return () => window.clearTimeout(timeoutId);
  }, [newStudentRowId]);

  useEffect(() => {
    if (!selectorHighlightSignal || flow || isOverlayOpen) return;

    const selector = selectorRef.current;
    if (!selector) return;

    const scrollTimeoutId = window.setTimeout(() => {
      selector.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 80);

    return () => {
      window.clearTimeout(scrollTimeoutId);
    };
  }, [flow, isOverlayOpen, selectorHighlightSignal]);

  const userClient = useMemo(
    () => ctx.clients.find((client) => client.authId === ctx.user?.uid) ?? null,
    [ctx.clients, ctx.user?.uid],
  );

  const school = useMemo(
    () => ctx.schools.find((item) => item.id === userClient?.schoolId) ?? null,
    [ctx.schools, userClient?.schoolId],
  );

  const placeSuggestions = useMemo(
    () => getSchoolPlaceCandidates(school),
    [school],
  );

  const books = useMemo<BookOption[]>(() => {
    if (flow === "kids") {
      return (ctx.booksKids ?? [])
        .filter(
          (book): book is KidsBook =>
            typeof book.num === "number" &&
            book.num >= 1 &&
            book.num <= MAX_KIDS_BOOK_NUM,
        )
        .map((book) => {
          const subtitle = kidsBookTitles[book.num];
          const code = `K${book.num}`;

          return {
            id: book.id ?? code,
            code,
            title: `English Unlocked Kids ${book.num}${
              subtitle ? ` - ${subtitle}` : ""
            }`,
            level: KIDS_BOOK_LEVELS[book.num] ?? code,
            sortOrder: book.num,
            flow,
            coverSrc: kidsBookCovers[book.num],
          };
        })
        .sort((a, b) => a.sortOrder - b.sortOrder);
    }

    if (flow === "adults") {
      return (ctx.books ?? [])
        .filter(
          (book): book is BookNew =>
            typeof book.bookNum === "number" &&
            book.bookNum >= 1 &&
            book.bookNum <= MAX_ADULT_BOOK_NUM,
        )
        .map((book) => {
          const code = `EU${book.bookNum}`;

          return {
            id: book.id,
            code,
            title: `English Unlocked ${book.bookNum}`,
            level: ADULT_BOOK_LEVELS[book.bookNum] ?? code,
            sortOrder: book.bookNum,
            flow,
            coverSrc: adultBookCovers[book.bookNum],
          };
        })
        .sort((a, b) => a.sortOrder - b.sortOrder);
    }

    return [];
  }, [ctx.books, ctx.booksKids, flow]);

  const selectedBook = useMemo(
    () => books.find((book) => book.id === selectedBookId) ?? null,
    [books, selectedBookId],
  );

  const previewStudent = useMemo(() => {
    const firstFilledStudent = studentRows.find(
      (student) =>
        normalizeSpaces(student.firstName) || normalizeSpaces(student.surname),
    );

    if (!firstFilledStudent) return DEFAULT_PREVIEW_STUDENT;

    return {
      firstName: normalizeSpaces(firstFilledStudent.firstName) || "YOUR",
      surname: normalizeSpaces(firstFilledStudent.surname) || "STUDENT",
    };
  }, [studentRows]);

  const currentCertificateData = useMemo<CertificateData | null>(() => {
    if (!flow || !selectedBook) return null;

    return {
      flow,
      orientation,
      issueDate,
      place: normalizeSpaces(place),
      schoolName: normalizeSpaces(schoolName),
      book: selectedBook,
      issuerName: normalizeSpaces(issuerName),
      schoolLogoDataUrl,
      directPressLogoDataUrl,
      directPressLogoSvg,
    };
  }, [
    directPressLogoDataUrl,
    directPressLogoSvg,
    flow,
    issueDate,
    issuerName,
    orientation,
    place,
    schoolLogoDataUrl,
    schoolName,
    selectedBook,
  ]);

  const existingStudentsByKey = useMemo(() => {
    const lookup = new Map<string, CertificateStudent>();

    certificateStudents.forEach((student) => {
      lookup.set(
        `${student.firstNameNormalized}__${student.surnameNormalized}`,
        student,
      );
    });

    return lookup;
  }, [certificateStudents]);

  const sessionDuplicateKeys = useMemo(() => {
    const counts = new Map<string, number>();

    studentRows.forEach((student) => {
      if (
        !normalizeSpaces(student.firstName) ||
        !normalizeSpaces(student.surname)
      ) {
        return;
      }

      const key = getStudentKey(student.firstName, student.surname);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });

    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([key]) => key),
    );
  }, [studentRows]);

  const certificateStudentSuggestions = useMemo(
    () =>
      certificateStudents
        .map((student) => ({
          key: `${student.firstNameNormalized}__${student.surnameNormalized}`,
          label: `${normalizeSpaces(student.firstName)} ${normalizeSpaces(
            student.surname,
          )}`.trim(),
          student,
        }))
        .filter((item) => item.label)
        .sort((a, b) =>
          a.label.localeCompare(b.label, "pl", { sensitivity: "base" }),
        ),
    [certificateStudents],
  );

  const certificateStudentSuggestionsByLabel = useMemo(() => {
    const lookup = new Map<string, CertificateStudent>();

    certificateStudentSuggestions.forEach((item) => {
      lookup.set(normalizeStudentValue(item.label), item.student);
    });

    return lookup;
  }, [certificateStudentSuggestions]);

  useEffect(() => {
    let isMounted = true;

    const loadLogo = async () => {
      try {
        const assets = await urlToImageAssets(directPressLogo);
        if (isMounted) {
          setDirectPressLogoDataUrl(assets.dataUrl);
          setDirectPressLogoSvg(assets.svg);
        }
      } catch (error) {
        console.error("Nie udało się pobrać logo Direct Press:", error);
        if (isMounted) {
          setDirectPressLogoDataUrl(null);
          setDirectPressLogoSvg(null);
        }
      }
    };

    void loadLogo();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadSchoolLogo = async () => {
      if (!isOverlayOpen || !flow) {
        if (isMounted) {
          setSchoolLogoDataUrl(null);
          setSchoolLogoStatus("idle");
        }
        return;
      }

      if (!userClient?.logoUrl) {
        if (isMounted) {
          setSchoolLogoDataUrl(null);
          setSchoolLogoStatus("missing");
        }
        return;
      }

      setSchoolLogoStatus("loading");
      const result = await getCertificateLogoForClient(userClient);

      if (!isMounted) return;

      setSchoolLogoDataUrl(result.dataUrl);
      setSchoolLogoStatus(result.status);

      if (result.status === "failed") {
        console.warn(
          "Nie udało się przygotować logo szkoły do PDF:",
          result.error,
        );
      }
    };

    void loadSchoolLogo();

    return () => {
      isMounted = false;
    };
  }, [flow, isOverlayOpen, userClient]);

  useEffect(() => {
    if (!schoolName && school?.name) {
      setSchoolName(school.name);
    }
  }, [school?.name, schoolName]);

  useEffect(() => {
    if (!place && placeSuggestions.length > 0) {
      setPlace(placeSuggestions[0]);
    }
  }, [place, placeSuggestions]);

  useEffect(() => {
    if (!selectedBookId || !books.some((book) => book.id === selectedBookId)) {
      setSelectedBookId(books[0]?.id ?? "");
    }
  }, [books, selectedBookId]);

  useEffect(() => {
    let isMounted = true;

    const loadCertificateStudents = async () => {
      if (!userClient?.id) {
        setCertificateStudents([]);
        setIsLoadingCertificateStudents(false);
        return;
      }

      setIsLoadingCertificateStudents(true);

      try {
        const snapshot = await getDocs(
          collection(
            db,
            "clients",
            userClient.id,
            CERTIFICATE_STUDENTS_COLLECTION,
          ),
        );
        const loadedStudents = snapshot.docs.map((studentDoc) => {
          const data = studentDoc.data() as Omit<CertificateStudent, "id">;
          return {
            id: studentDoc.id,
            ...data,
          };
        });

        if (isMounted) setCertificateStudents(loadedStudents);
      } catch (error) {
        console.error(
          "Nie udało się pobrać bazy uczniów do certyfikatów:",
          error,
        );
        if (isMounted) {
          setCertificateStudents([]);
          setMessage({
            type: "error",
            text: "Nie udało się pobrać bazy uczniów.",
          });
        }
      } finally {
        if (isMounted) setIsLoadingCertificateStudents(false);
      }
    };

    void loadCertificateStudents();

    return () => {
      isMounted = false;
    };
  }, [userClient?.id]);

  useEffect(() => {
    return () => {
      if (previewPdfUrl) {
        URL.revokeObjectURL(previewPdfUrl);
      }
    };
  }, [previewPdfUrl]);

  useEffect(() => {
    if (!isStudentPanelOpen) return;

    window.requestAnimationFrame(() => {
      const studentList = studentListRef.current;
      if (!studentList) return;

      studentList.scrollTop = studentList.scrollHeight;
    });
  }, [isStudentPanelOpen, studentRows.length]);

  const resetReview = () => {
    setReviewItems([]);
    setGenerationConfirmation(null);
    setReviewData(null);
    setIsReviewVisible(false);
    setPreviewPdfUrl(null);
    setPreviewModalIndex(null);
  };

  const handleFlowSelect = (nextFlow: CertificateFlow) => {
    setFlow(nextFlow);
    setIsOverlayOpen(true);
    setIsStudentPanelOpen(false);
    setStudentRows([]);
    setHasAddedStudentInSession(false);
    setValidationErrors([]);
    setMessage(null);
    resetReview();
  };

  const handleCloseOverlay = () => {
    setIsOverlayOpen(false);
    setFlow(null);
    setHasAddedStudentInSession(false);
    setNewStudentRowId(null);
    setValidationErrors([]);
    setMessage(null);
    resetReview();
  };

  const updateStudentRow = (
    rowId: string,
    field: "firstName" | "surname",
    value: string,
  ) => {
    const selectedStudent = certificateStudentSuggestionsByLabel.get(
      normalizeStudentValue(value),
    );

    setStudentRows((currentRows) =>
      currentRows.map((row) =>
        row.id === rowId
          ? selectedStudent
            ? {
                ...row,
                firstName: selectedStudent.firstName,
                surname: selectedStudent.surname,
              }
            : {
                ...row,
                [field]: value,
              }
          : row,
      ),
    );
  };

  const addStudentRow = () => {
    const newRow = createStudentRow();

    setHasAddedStudentInSession(true);
    setNewStudentRowId(newRow.id);
    setStudentRows((currentRows) => [...currentRows, newRow]);
  };

  const removeStudentRow = (rowId: string) => {
    setStudentRows((currentRows) =>
      currentRows.filter((row) => row.id !== rowId),
    );
  };

  const upsertLocalCertificateStudent = (student: CertificateStudent) => {
    setCertificateStudents((currentStudents) => {
      const nextStudents = currentStudents.filter(
        (item) =>
          `${item.firstNameNormalized}__${item.surnameNormalized}` !==
          `${student.firstNameNormalized}__${student.surnameNormalized}`,
      );

      return [...nextStudents, student];
    });
  };

  const handleAddStudentToDatabase = async (row: StudentRow) => {
    if (!userClient?.id) {
      setMessage({
        type: "error",
        text: "Brak klienta.",
      });
      return;
    }

    const firstName = normalizeSpaces(row.firstName);
    const surname = normalizeSpaces(row.surname);

    if (!firstName || !surname) {
      setMessage({
        type: "error",
        text: "Uzupełnij imię i nazwisko.",
      });
      return;
    }

    const firstNameNormalized = normalizeStudentValue(firstName);
    const surnameNormalized = normalizeStudentValue(surname);
    const studentId = createCertificateStudentId(firstName, surname);
    const studentRef = doc(
      db,
      "clients",
      userClient.id,
      CERTIFICATE_STUDENTS_COLLECTION,
      studentId,
    );

    setSavingStudentRowId(row.id);
    setMessage(null);

    try {
      const existingSnapshot = await getDoc(studentRef);

      if (existingSnapshot.exists()) {
        const existingData = existingSnapshot.data() as Omit<
          CertificateStudent,
          "id"
        >;

        upsertLocalCertificateStudent({
          id: existingSnapshot.id,
          ...existingData,
        });
        setMessage({
          type: "info",
          text: "Uczeń jest już w bazie.",
        });
        return;
      }

      const payload: Omit<CertificateStudent, "id"> = {
        firstName,
        surname,
        firstNameNormalized,
        surnameNormalized,
        createdAt: Date.now(),
        createdByAdminUid: ctx.user?.uid,
      };

      await setDoc(studentRef, payload);
      upsertLocalCertificateStudent({
        id: studentId,
        ...payload,
      });
      if (flow) {
        void trackEventWithoutThrottle(ctx, "certificate_student_added", {
          screen: "ManagmentScreen",
          path: "/zarzadzanie",
          meta: {
            feature: "certificate_generator",
            clientId: userClient.id,
            clientName: userClient.name,
            schoolId: userClient.schoolId ?? null,
            schoolName: school?.name ?? normalizeSpaces(schoolName) ?? null,
            flow,
            firstName,
            surname,
            firstNameNormalized,
            surnameNormalized,
            certificateStudentId: studentId,
          },
        });
      }
      setMessage({
        type: "success",
        text: "Dodano do bazy.",
      });
    } catch (error) {
      console.error(
        "Nie udało się zapisać ucznia do bazy certyfikatów:",
        error,
      );
      setMessage({
        type: "error",
        text: "Nie udało się zapisać ucznia.",
      });
    } finally {
      setSavingStudentRowId(null);
    }
  };

  const validateForm = () => {
    const errors: string[] = [];
    const trimmedStudents = studentRows.map((student) => ({
      ...student,
      firstName: normalizeSpaces(student.firstName),
      surname: normalizeSpaces(student.surname),
    }));

    if (!flow) errors.push("Wybierz typ dokumentu.");
    if (!issueDate) errors.push("Data jest wymagana.");
    if (!selectedBook) errors.push("Poziom jest wymagany.");
    if (!normalizeSpaces(issuerName)) {
      errors.push(ISSUER_NAME_VALIDATION_MESSAGE);
    }
    if (isLoadingCertificateStudents) errors.push("Trwa ładowanie bazy uczniów.");
    if (userClient?.logoUrl && schoolLogoStatus === "loading") {
      errors.push("Trwa przygotowywanie logo szkoły do PDF.");
    }

    if (trimmedStudents.length === 0) {
      errors.push(NO_STUDENT_VALIDATION_MESSAGE);
    }

    if (
      trimmedStudents.some((student) => !student.firstName || !student.surname)
    ) {
      errors.push(INCOMPLETE_STUDENT_VALIDATION_MESSAGE);
    }

    if (sessionDuplicateKeys.size > 0) {
      errors.push("Ten sam uczeń występuje więcej niż raz.");
    }

    return errors;
  };

  const handlePrepareReview = () => {
    const errors = validateForm();
    setValidationErrors(errors);
    setMessage(null);

    if (errors.length > 0 || !currentCertificateData) return;

    const nextReviewItems = studentRows.map((student) => ({
      id: createLocalId(),
      firstName: normalizeSpaces(student.firstName),
      surname: normalizeSpaces(student.surname),
    }));

    setPreviewPdfUrl(null);
    setPreviewModalIndex(null);
    setReviewData(currentCertificateData);
    setReviewItems(nextReviewItems);
    setGenerationConfirmation({
      id: createLocalId(),
      count: nextReviewItems.length,
    });
    setIsReviewVisible(true);
    setIsStudentPanelOpen(false);
  };

  const updateReviewItem = (
    itemId: string,
    field: "firstName" | "surname",
    value: string,
  ) => {
    setReviewItems((currentItems) =>
      currentItems.map((item) =>
        item.id === itemId
          ? {
              ...item,
              [field]: value,
            }
          : item,
      ),
    );
  };

  const deleteReviewItem = (itemId: string) => {
    const nextReviewItems = reviewItems.filter((item) => item.id !== itemId);

    setReviewItems(nextReviewItems);

    if (nextReviewItems.length === 0) {
      setIsReviewVisible(false);
      setReviewData(null);
      setPreviewPdfUrl(null);
      setPreviewModalIndex(null);
    }
  };

  const validateReviewItems = () => {
    const errors: string[] = [];
    const counts = new Map<string, number>();

    if (!reviewData) errors.push("Przygotuj listę.");
    if (reviewItems.length === 0) errors.push("Lista jest pusta.");

    reviewItems.forEach((item, index) => {
      const firstName = normalizeSpaces(item.firstName);
      const surname = normalizeSpaces(item.surname);

      if (!firstName || !surname) {
        errors.push(`Uzupełnij pozycję nr ${index + 1}.`);
        return;
      }

      const key = getStudentKey(firstName, surname);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });

    if (Array.from(counts.values()).some((count) => count > 1)) {
      errors.push("Na liście jest duplikat ucznia.");
    }

    return errors;
  };

  const handlePreviewPdf = async (item: ReviewItem, itemIndex?: number) => {
    if (!reviewData) return;

    if (typeof itemIndex === "number") {
      setPreviewModalIndex(itemIndex);
    }
    setPreviewingItemId(item.id);
    setPreviewPdfUrl(null);
    setMessage(null);

    try {
      const blob = await buildGeneratedDocumentBlob(reviewData, {
        ...item,
        firstName: normalizeSpaces(item.firstName),
        surname: normalizeSpaces(item.surname),
      });
      const url = URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
    } catch (error) {
      console.error("Nie udało się wygenerować podglądu:", error);
      setMessage({
        type: "error",
        text: "Błąd podglądu.",
      });
    } finally {
      setPreviewingItemId(null);
    }
  };

  const closePreviewModal = () => {
    setPreviewModalIndex(null);
    setPreviewingItemId(null);
    setPreviewPdfUrl(null);
  };

  const handlePreviewNavigation = (itemIndex: number) => {
    const nextItem = reviewItems[itemIndex];
    if (!nextItem) return;

    void handlePreviewPdf(nextItem, itemIndex);
  };

  const handleFinalDownload = async () => {
    const errors = validateReviewItems();
    setValidationErrors(errors);
    setMessage(null);

    if (errors.length > 0 || !reviewData) return;

    setIsDownloading(true);

    try {
      const normalizedItems = reviewItems.map((item) => ({
        ...item,
        firstName: normalizeSpaces(item.firstName),
        surname: normalizeSpaces(item.surname),
      }));
      const outputFormat = getGeneratedDocumentFormat(reviewData);

      if (normalizedItems.length === 1) {
        const documentBlob = await buildGeneratedDocumentBlob(
          reviewData,
          normalizedItems[0],
        );
        downloadBlob(
          documentBlob,
          buildGeneratedDocumentFilename(reviewData, normalizedItems[0]),
        );
      } else {
        const zip = new JSZip();

        for (const item of normalizedItems) {
          const documentBlob = await buildGeneratedDocumentBlob(
            reviewData,
            item,
          );
          zip.file(
            buildGeneratedDocumentFilename(reviewData, item),
            documentBlob,
          );
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });
        downloadBlob(
          zipBlob,
          buildZipFilename(reviewData, normalizedItems.length),
        );
      }

      void trackEventWithoutThrottle(ctx, "certificate_download", {
        screen: "ManagmentScreen",
        path: "/zarzadzanie",
        meta: {
          feature: "certificate_generator",
          clientId: userClient?.id ?? null,
          clientName: userClient?.name ?? null,
          schoolId: userClient?.schoolId ?? null,
          schoolName: reviewData.schoolName || school?.name || null,
          flow: reviewData.flow,
          documentKind: "certyfikat",
          bookId: reviewData.book.id,
          bookCode: reviewData.book.code,
          bookTitle: reviewData.book.title,
          bookLevel: reviewData.book.level,
          documentCount: normalizedItems.length,
          downloadType: normalizedItems.length > 1 ? "zip" : outputFormat,
          issueDate: reviewData.issueDate,
          place: reviewData.place,
          students: normalizedItems.map((student) => ({
            firstName: student.firstName,
            surname: student.surname,
            firstNameNormalized: normalizeStudentValue(student.firstName),
            surnameNormalized: normalizeStudentValue(student.surname),
          })),
        },
      });
    } catch (error) {
      console.error("Nie udało się wygenerować pliku:", error);
      setMessage({
        type: "error",
        text:
          reviewItems.length > 1
            ? "Błąd ZIP."
            : reviewData && getGeneratedDocumentFormat(reviewData) === "png"
              ? "Błąd PNG."
              : "Błąd PDF.",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const renderMessage = (currentMessage: Message | null) => {
    if (!currentMessage) return null;

    const className =
      currentMessage.type === "error"
        ? "border-rose-200 bg-rose-50/80 text-rose-700"
        : currentMessage.type === "success"
          ? "border-emerald-200 bg-emerald-50/80 text-emerald-700"
          : currentMessage.type === "warning"
            ? "border-amber-200 bg-amber-50/80 text-amber-800"
            : "border-sky-200 bg-sky-50/80 text-sky-700";

    return (
      <div className={`rounded-2xl border px-4 py-3 text-sm ${className}`}>
        {currentMessage.text}
      </div>
    );
  };

  if (!flow || !isOverlayOpen) {
    return (
      <div className="w-full">
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white/85 p-5 shadow-[0_18px_45px_rgba(15,23,42,0.08)] backdrop-blur-sm sm:p-6">
          <h2 className="text-xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-2xl">
            Automatyczne tworzenie certyfikatów
          </h2>
          <p className="mt-3 max-w-4xl text-sm leading-6 text-slate-700 sm:text-base sm:leading-7">
            Za pomocą naszego generatora szybko przygotujesz profesjonalne
            certyfikaty dla swoich uczniów. Wystarczy wpisać imiona i nazwiska,
            wybrać rodzaj certyfikatu i pobrać gotowe pliki do druku.
          </p>
        </div>
        <div ref={selectorRef} className="grid gap-4 md:grid-cols-2">
          <button
            type="button"
            onClick={() => handleFlowSelect("kids")}
            className="group relative overflow-hidden rounded-[30px] border border-white/50 bg-white/20 p-5 text-center shadow-[0_18px_44px_rgba(148,163,184,0.16)] transition duration-300 hover:-translate-y-1 hover:border-white hover:bg-white/45 hover:shadow-[0_24px_70px_rgba(148,163,184,0.24)]"
          >
            <div className="relative mx-auto flex h-80 w-full items-center justify-center">
              <img
                src={certificateKidsPreview}
                alt=""
                className="relative h-full w-full origin-center rotate-1 rounded-[22px] object-contain drop-shadow-[0_22px_28px_rgba(15,23,42,0.22)] transition duration-300 group-hover:rotate-0 group-hover:scale-[1.03]"
              />
            </div>
            <div className="relative z-10 mt-5 inline-flex items-center justify-center gap-3 rounded-full border border-white/60 bg-white/55 px-5 py-3 text-sm font-semibold text-slate-900 shadow-sm backdrop-blur-sm">
              <span>Otwórz generator</span>
              <ChevronRight
                size={18}
                strokeWidth={2.4}
                className="transition group-hover:translate-x-1"
              />
            </div>
            <p className="relative z-10 mx-auto mt-4 bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-base font-semibold tracking-[-0.02em] text-transparent">
              Certyfikaty dla dzieci
            </p>
          </button>

          <button
            type="button"
            onClick={() => handleFlowSelect("adults")}
            className="group relative overflow-hidden rounded-[30px] border border-white/50 bg-white/20 p-5 text-center transition duration-300 hover:-translate-y-1 hover:border-white hover:bg-white/45 hover:shadow-[0_24px_70px_rgba(148,163,184,0.24)]"
          >
            <div className="relative mx-auto flex h-80 w-full  items-center justify-center">
              <img
                src={certificateAdultsPreview}
                alt=""
                className="relative h-full w-full origin-center rotate-1 rounded-[22px] object-contain drop-shadow-[0_22px_28px_rgba(15,23,42,0.22)] transition duration-300 group-hover:rotate-0 group-hover:scale-[1.03]"
              />
            </div>
            <div className="relative z-10 mt-5 inline-flex items-center justify-center gap-3 rounded-full border border-white/60 bg-white/55 px-5 py-3 text-sm font-semibold text-slate-900 shadow-sm backdrop-blur-sm">
              <span>Otwórz generator</span>
              <ChevronRight
                size={18}
                strokeWidth={2.4}
                className="transition group-hover:translate-x-1"
              />
            </div>
            <p className="relative z-10 mx-auto mt-4 bg-[linear-gradient(135deg,#0f172a_10%,#2563eb_48%,#334155_92%)] bg-clip-text text-base font-semibold tracking-[-0.02em] text-transparent">
              Certyfikaty dla młodzieży/dorosłych
            </p>
          </button>
        </div>
      </div>
    );
  }

  const flowConfig = getFlowConfig(flow);
  const generatorOverlayTitle =
    flow === "kids"
      ? "Generator certyfikatów dla dzieci"
      : "Generator certyfikatów dla młodzieży oraz dorosłych";
  const activePreviewItem =
    previewModalIndex === null
      ? null
      : (reviewItems[previewModalIndex] ?? null);
  const isPreviewImage = Boolean(
    reviewData && shouldUseKidsImageTemplate(reviewData),
  );
  const studentCountLabel = `${studentRows.length} ${
    studentRows.length === 1 ? "uczeń" : "uczniów"
  }`;
  const isIssuerNameMissing = !normalizeSpaces(issuerName);
  const hasNoStudents = studentRows.length === 0;
  const showAddStudentValidation =
    hasNoStudents && validationErrors.includes(NO_STUDENT_VALIDATION_MESSAGE);
  const showIncompleteStudentValidation = validationErrors.includes(
    INCOMPLETE_STUDENT_VALIDATION_MESSAGE,
  );
  const showIssuerNameValidation =
    isIssuerNameMissing &&
    validationErrors.includes(ISSUER_NAME_VALIDATION_MESSAGE);
  const topValidationErrors = validationErrors.filter(
    (error) =>
      error !== NO_STUDENT_VALIDATION_MESSAGE &&
      error !== INCOMPLETE_STUDENT_VALIDATION_MESSAGE &&
      error !== ISSUER_NAME_VALIDATION_MESSAGE,
  );
  const isGenerateDisabled =
    isLoadingCertificateStudents ||
    (Boolean(userClient?.logoUrl) && schoolLogoStatus === "loading");
  const isGenerateButtonUnavailable = isGenerateDisabled || hasNoStudents;

  const handleGenerateButtonClick = () => {
    if (hasNoStudents) {
      setMessage(null);
      setValidationErrors([NO_STUDENT_VALIDATION_MESSAGE]);
      return;
    }

    handlePrepareReview();
  };

  const studentEditorContent = (
    <>
      <datalist id="certificate-students">
        {certificateStudentSuggestions.map((student) => (
          <option key={student.key} value={student.label} />
        ))}
      </datalist>

      <div
        ref={studentListRef}
        className="max-h-[calc(100vh-18rem)] space-y-2 overflow-y-auto overscroll-contain pr-1"
      >
        {studentRows.length === 0 ? (
          <div className="flex min-h-[240px] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 text-center text-sm font-semibold text-slate-500">
            Dodaj ucznia, by wygenerować certyfikat.
          </div>
        ) : (
          studentRows.map((row, index) => {
            const studentKey = getStudentKey(row.firstName, row.surname);
            const hasBothNames =
              Boolean(normalizeSpaces(row.firstName)) &&
              Boolean(normalizeSpaces(row.surname));
            const firstNameMissing =
              showIncompleteStudentValidation &&
              !normalizeSpaces(row.firstName);
            const surnameMissing =
              showIncompleteStudentValidation && !normalizeSpaces(row.surname);
            const existingStudent = existingStudentsByKey.get(studentKey);
            const isDuplicate = sessionDuplicateKeys.has(studentKey);

            return (
              <div
                key={row.id}
                className={`grid gap-2 rounded-2xl border px-3 py-2.5 transition-colors sm:grid-cols-[32px_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-center ${
                  newStudentRowId === row.id
                    ? "animate-[certificateStudentRowCreated_850ms_ease-out]"
                    : ""
                } ${
                  isDuplicate
                    ? "border-amber-200/80 bg-amber-50/90"
                    : "border-slate-200/60 bg-white/70 hover:bg-white/90"
                }`}
              >
                <div className="flex h-10 w-8 items-center justify-center text-sm font-semibold text-slate-400">
                  {index + 1}.
                </div>

                <input
                  type="text"
                  list="certificate-students"
                  placeholder={firstNameMissing ? "Wymagane: imię" : "Imię"}
                  value={row.firstName}
                  onChange={(event) =>
                    updateStudentRow(row.id, "firstName", event.target.value)
                  }
                  aria-invalid={firstNameMissing}
                  className={`min-w-0 rounded-xl border px-3 py-2.5 text-sm text-slate-800 outline-none transition ${
                    firstNameMissing
                      ? "border-rose-300 bg-rose-50/70 placeholder:text-rose-400 focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
                      : "border-slate-200 bg-white focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                  }`}
                />

                <input
                  type="text"
                  list="certificate-students"
                  placeholder={
                    surnameMissing ? "Wymagane: nazwisko" : "Nazwisko"
                  }
                  value={row.surname}
                  onChange={(event) =>
                    updateStudentRow(row.id, "surname", event.target.value)
                  }
                  aria-invalid={surnameMissing}
                  className={`min-w-0 rounded-xl border px-3 py-2.5 text-sm text-slate-800 outline-none transition ${
                    surnameMissing
                      ? "border-rose-300 bg-rose-50/70 placeholder:text-rose-400 focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
                      : "border-slate-200 bg-white focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                  }`}
                />

                <div className="flex flex-wrap items-center justify-end gap-2">
                  {hasBothNames && existingStudent && (
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      W bazie
                    </span>
                  )}

                  {isDuplicate && (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
                      Duplikat
                    </span>
                  )}

                  {hasBothNames && !existingStudent && (
                    <button
                      type="button"
                      onClick={() => handleAddStudentToDatabase(row)}
                      disabled={savingStudentRowId === row.id}
                      className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-700 transition hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <UserPlus size={15} strokeWidth={2.2} />
                      {savingStudentRowId === row.id
                        ? "Zapisywanie"
                        : "Dodaj do bazy"}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => removeStudentRow(row.id)}
                    className="flex h-9 w-9 items-center justify-center text-slate-500 transition hover:text-rose-500"
                    aria-label={`Usuń ucznia ${index + 1}`}
                  >
                    <Trash2 size={16} strokeWidth={2.2} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 z-[80] overflow-y-auto bg-slate-50">
      <style>
        {`
          @keyframes certificateStudentRowCreated {
            0% {
              opacity: 0;
              transform: translateY(8px) scale(0.985);
              background: #dbeafe;
              border-color: #60a5fa;
              box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.22), 0 16px 34px rgba(37, 99, 235, 0.14);
            }
            45% {
              opacity: 1;
              transform: translateY(0) scale(1);
              background: #eff6ff;
              border-color: #93c5fd;
              box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14), 0 10px 24px rgba(37, 99, 235, 0.1);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
              background: rgba(255, 255, 255, 0.7);
              border-color: rgba(226, 232, 240, 0.6);
              box-shadow: none;
            }
          }
        `}
      </style>
      <div className="relative z-10 w-full space-y-5 px-4 pb-36 pt-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleCloseOverlay}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
            aria-label="Zamknij generator"
          >
            <X size={20} strokeWidth={2.3} />
          </button>
          <h1 className="text-xl font-semibold tracking-[-0.03em] text-slate-900 sm:text-2xl">
            {generatorOverlayTitle}
          </h1>
        </div>

        {renderMessage(message)}

        {topValidationErrors.length > 0 && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50/80 px-4 py-3 text-sm text-rose-700">
            <ul className="list-disc space-y-1 pl-5">
              {topValidationErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid gap-6 xl:grid-cols-[minmax(440px,0.8fr)_minmax(680px,1.2fr)]">
          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">
                  1
                </span>
                <p className="text-lg font-semibold text-slate-900">
                  Wypełnij dane
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">
                    Data
                  </span>
                  <input
                    type="date"
                    value={issueDate}
                    onChange={(event) => setIssueDate(event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-800 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">
                    Miejsce
                  </span>
                  <datalist id="certificate-place-suggestions">
                    {placeSuggestions.map((candidate) => (
                      <option key={candidate} value={candidate} />
                    ))}
                  </datalist>
                  <input
                    type="text"
                    list={
                      placeSuggestions.length > 0
                        ? "certificate-place-suggestions"
                        : undefined
                    }
                    value={place}
                    onChange={(event) => setPlace(event.target.value)}
                    placeholder={
                      placeSuggestions.length > 1
                        ? "Wybierz albo wpisz ręcznie"
                        : "Wpisz miejsce"
                    }
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-800 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                  />
                </label>

                <div className="block md:col-span-2">
                  <span className="text-sm font-semibold text-slate-700">
                    Osoba wystawiająca certyfikat
                  </span>

                  <input
                    type="text"
                    value={issuerName}
                    onChange={(event) => setIssuerName(event.target.value)}
                    placeholder={
                      showIssuerNameValidation
                        ? "Wymagane: osoba wystawiająca certyfikat"
                        : "Imię i nazwisko"
                    }
                    aria-invalid={showIssuerNameValidation}
                    className={`mt-2 w-full rounded-2xl border bg-white px-4 py-3 text-slate-800 outline-none transition ${
                      showIssuerNameValidation
                        ? "border-rose-300 placeholder:text-rose-400 focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
                        : "border-slate-200 focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                    }`}
                  />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">
                  2
                </span>
                <p className="text-lg font-semibold text-slate-900">
                          Wybierz parametry
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">

                <div className="block md:col-span-2">
                  <span className="text-sm font-semibold text-slate-700">
                    Układ strony
                  </span>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setOrientation("landscape")}
                      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${
                        orientation === "landscape"
                          ? "border-emerald-300 bg-emerald-50 text-emerald-900 shadow-[0_10px_24px_rgba(16,185,129,0.16)]"
                          : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-900"
                      }`}
                    >
                      <span className="flex h-10 w-14 shrink-0 items-center justify-center rounded-xl border border-current/25 bg-white">
                        <span className="h-5 w-9 rounded border-2 border-current" />
                      </span>
                      <span>Poziomo</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setOrientation("portrait")}
                      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${
                        orientation === "portrait"
                          ? "border-emerald-300 bg-emerald-50 text-emerald-900 shadow-[0_10px_24px_rgba(16,185,129,0.16)]"
                          : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-900"
                      }`}
                    >
                      <span className="flex h-10 w-14 shrink-0 items-center justify-center rounded-xl border border-current/25 bg-white">
                        <span className="h-8 w-5 rounded border-2 border-current" />
                      </span>
                      <span>Pionowo</span>
                    </button>
                  </div>
                </div>

                <div className="block md:col-span-2">
                  <span className="text-sm font-semibold text-slate-700">
                    Poziom
                  </span>
                  <div className="mt-3 grid grid-cols-5 gap-3 sm:grid-cols-7">
                    {books.map((book) => {
                      const selected = selectedBookId === book.id;

                      return (
                        <button
                          key={book.id}
                          type="button"
                          onClick={() => setSelectedBookId(book.id)}
                          aria-label={book.code}
                          className={`group relative overflow-hidden rounded-2xl border bg-white p-1.5 shadow-[0_10px_24px_rgba(148,163,184,0.12)] transition hover:-translate-y-0.5 hover:bg-white ${
                            selected
                              ? "border-emerald-400 ring-4 ring-emerald-100"
                              : "border-slate-200/80"
                          }`}
                        >
                          {book.coverSrc ? (
                            <img
                              src={book.coverSrc}
                              alt={book.code}
                              className="aspect-[3/4] w-full rounded-xl object-cover"
                            />
                          ) : (
                            <span className="flex aspect-[3/4] w-full items-center justify-center rounded-xl bg-slate-100 text-xs font-bold text-slate-500">
                              {book.code}
                            </span>
                          )}
                          <span className="mt-1.5 block text-center text-[11px] font-bold text-slate-600">
                            Book {book.sortOrder}
                          </span>
                          {selected && (
                            <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-sm font-black text-white shadow-lg shadow-emerald-500/30">
                              ✓
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-5 xl:sticky xl:top-6 xl:self-start">
            {isReviewVisible && reviewItems.length > 0 ? (
              <div className="flex max-h-[calc(100vh-8rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <div className="sticky top-0 z-10 mb-5 flex shrink-0 flex-wrap items-center justify-between gap-3 bg-white">
                  <div className="flex items-center gap-3">
                    <p className="text-lg font-semibold text-slate-900">
                      Gotowe certyfikaty
                    </p>
                    <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-bold text-emerald-800">
                      {reviewItems.length}
                    </span>
                    <button
                      type="button"
                      onClick={resetReview}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-white hover:text-slate-900"
                    >
                      Wyczyść listę
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleFinalDownload}
                    disabled={isDownloading || reviewItems.length === 0}
                    className={`inline-flex min-w-[158px] items-center justify-center gap-2 rounded-full px-5 py-3 ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
                    style={getActionButtonStyle("sky")}
                  >
                    {reviewItems.length > 1 ? (
                      <FileArchive size={16} strokeWidth={2.2} />
                    ) : (
                      <Download size={16} strokeWidth={2.2} />
                    )}
                    {isDownloading
                      ? "Pobieranie"
                      : reviewItems.length > 1
                        ? "Pobierz ZIP"
                        : reviewData &&
                            getGeneratedDocumentFormat(reviewData) === "png"
                          ? "Pobierz PNG"
                          : "Pobierz PDF"}
                  </button>
                </div>

                {generationConfirmation && (
                  <div
                    key={generationConfirmation.id}
                    className="mb-4 overflow-hidden rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-900 shadow-[0_18px_34px_rgba(16,185,129,0.14)]"
                    style={{
                      animation:
                        "certificateGenerationConfirmation 3200ms ease-in-out forwards",
                    }}
                  >
                    <style>
                      {`
                        @keyframes certificateGenerationConfirmation {
                          0% { opacity: 0; transform: translateY(-10px) scale(0.98); }
                          12% { opacity: 1; transform: translateY(0) scale(1); }
                          78% { opacity: 1; transform: translateY(0) scale(1); }
                          100% { opacity: 0; transform: translateY(-8px) scale(0.985); }
                        }
                        @keyframes certificateGenerationCheck {
                          0% { transform: scale(0.5); box-shadow: 0 0 0 0 rgba(16,185,129,0.45); }
                          45% { transform: scale(1.08); box-shadow: 0 0 0 12px rgba(16,185,129,0); }
                          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(16,185,129,0); }
                        }
                      `}
                    </style>
                    <div className="flex items-center gap-3">
                      <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-lg font-black text-white"
                        style={{
                          animation:
                            "certificateGenerationCheck 720ms ease-out both",
                        }}
                      >
                        ✓
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-bold">
                          Certyfikaty wygenerowane
                        </p>
                        <p className="text-sm text-emerald-800">
                          {generationConfirmation.count === 1
                            ? "1 certyfikat gotowy do sprawdzenia i pobrania."
                            : `${generationConfirmation.count} certyfikatów gotowych do sprawdzenia i pobrania.`}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {reviewItems.length === 0 ? (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-4 text-sm text-amber-800">
                    Lista jest pusta.
                  </div>
                ) : (
                  <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                    <div className="space-y-2">
                      {reviewItems.map((item, index) => (
                        <div
                          key={item.id}
                          className="grid gap-2 rounded-2xl border border-slate-200/70 bg-slate-50/80 px-3 py-2.5 transition sm:grid-cols-[32px_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-center"
                        >
                          <div className="flex h-10 w-8 items-center justify-center text-sm font-semibold text-slate-400">
                            {index + 1}.
                          </div>

                          <input
                            type="text"
                            placeholder="Imię"
                            value={item.firstName}
                            onChange={(event) =>
                              updateReviewItem(
                                item.id,
                                "firstName",
                                event.target.value,
                              )
                            }
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                          />

                          <input
                            type="text"
                            placeholder="Nazwisko"
                            value={item.surname}
                            onChange={(event) =>
                              updateReviewItem(
                                item.id,
                                "surname",
                                event.target.value,
                              )
                            }
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                          />

                          <div className="flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handlePreviewPdf(item, index)}
                              disabled={previewingItemId === item.id}
                              className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
                              style={getActionButtonStyle("sky")}
                            >
                              <Eye size={15} strokeWidth={2.2} />
                              Podgląd
                            </button>

                            <button
                              type="button"
                              onClick={() => deleteReviewItem(item.id)}
                              className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                            >
                              <Trash2 size={15} strokeWidth={2.2} />
                              Usuń
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : isStudentPanelOpen ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                {studentEditorContent}
              </div>
            ) : (
              <CertificatePreview
                data={currentCertificateData}
                flow={flow}
                previewStudent={previewStudent}
                showInitialPreview={false}
              />
            )}
          </div>

          <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex justify-center px-3 pb-3 sm:px-5 sm:pb-5">
            <div className="pointer-events-auto visible flex w-full max-w-3xl translate-y-0 flex-col rounded-[38px] border border-slate-300 bg-white opacity-100 shadow-[0_-18px_60px_rgba(15,23,42,0.22)] ring-1 ring-slate-900/10 transition-all duration-500 ease-out">
              {false && (
              <div
                className={`overflow-hidden transition-all duration-400 ease-out ${
                  isStudentPanelOpen
                    ? "max-h-[60vh] opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <div className="flex flex-col px-4 pt-4 sm:px-5 sm:pt-5">
                  <div className="mb-3 flex items-center justify-between px-3">
                    <p className="text-xs font-medium uppercase tracking-[0.1em] text-slate-700">
                      {flowConfig.studentLabel}
                    </p>
                    <p className="text-xs font-medium text-slate-500">
                      {studentCountLabel}
                    </p>
                  </div>

                  <datalist id="certificate-students">
                    {certificateStudentSuggestions.map((student) => (
                      <option key={student.key} value={student.label} />
                    ))}
                  </datalist>

                  <div
                    ref={studentListRef}
                    className="max-h-[calc(60vh-5rem)] space-y-2 overflow-y-auto overscroll-contain pr-1"
                  >
                    {studentRows.map((row, index) => {
                      const studentKey = getStudentKey(
                        row.firstName,
                        row.surname,
                      );
                      const hasBothNames =
                        Boolean(normalizeSpaces(row.firstName)) &&
                        Boolean(normalizeSpaces(row.surname));
                      const firstNameMissing =
                        showIncompleteStudentValidation &&
                        !normalizeSpaces(row.firstName);
                      const surnameMissing =
                        showIncompleteStudentValidation &&
                        !normalizeSpaces(row.surname);
                      const existingStudent =
                        existingStudentsByKey.get(studentKey);
                      const isDuplicate = sessionDuplicateKeys.has(studentKey);

                      return (
                        <div
                          key={row.id}
                          className={`grid gap-2 rounded-2xl border px-3 py-2.5 transition-colors sm:grid-cols-[32px_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-center ${
                            newStudentRowId === row.id
                              ? "animate-[certificateStudentRowCreated_850ms_ease-out]"
                              : ""
                          } ${
                            isDuplicate
                              ? "border-amber-200/80 bg-amber-50/90"
                              : "border-slate-200/60 bg-white/70 hover:bg-white/90"
                          }`}
                        >
                          <div className="flex h-10 w-8 items-center justify-center text-sm font-semibold text-slate-400">
                            {index + 1}.
                          </div>

                          <input
                            type="text"
                            list="certificate-students"
                            placeholder={
                              firstNameMissing ? "Wymagane: imię" : "Imię"
                            }
                            value={row.firstName}
                            onChange={(event) =>
                              updateStudentRow(
                                row.id,
                                "firstName",
                                event.target.value,
                              )
                            }
                            aria-invalid={firstNameMissing}
                            className={`min-w-0 rounded-xl border px-3 py-2.5 text-sm text-slate-800 outline-none transition ${
                              firstNameMissing
                                ? "border-rose-300 bg-rose-50/70 placeholder:text-rose-400 focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
                                : "border-slate-200 bg-white focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                            }`}
                          />

                          <input
                            type="text"
                            list="certificate-students"
                            placeholder={
                              surnameMissing ? "Wymagane: nazwisko" : "Nazwisko"
                            }
                            value={row.surname}
                            onChange={(event) =>
                              updateStudentRow(
                                row.id,
                                "surname",
                                event.target.value,
                              )
                            }
                            aria-invalid={surnameMissing}
                            className={`min-w-0 rounded-xl border px-3 py-2.5 text-sm text-slate-800 outline-none transition ${
                              surnameMissing
                                ? "border-rose-300 bg-rose-50/70 placeholder:text-rose-400 focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
                                : "border-slate-200 bg-white focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                            }`}
                          />

                          <div className="flex flex-wrap items-center justify-end gap-2">
                            {hasBothNames && existingStudent && (
                              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                                W bazie
                              </span>
                            )}

                            {isDuplicate && (
                              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
                                Duplikat
                              </span>
                            )}

                            {hasBothNames && !existingStudent && (
                              <button
                                type="button"
                                onClick={() => handleAddStudentToDatabase(row)}
                                disabled={savingStudentRowId === row.id}
                                className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-700 transition hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <UserPlus size={15} strokeWidth={2.2} />
                                {savingStudentRowId === row.id
                                  ? "Zapisywanie"
                                  : "Dodaj do bazy"}
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => removeStudentRow(row.id)}
                              className="flex h-9 w-9 items-center justify-center text-slate-500 transition hover:text-rose-500"
                              aria-label={`Usuń ucznia ${index + 1}`}
                            >
                              <Trash2 size={16} strokeWidth={2.2} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mx-4 mt-3 border-t border-slate-200/60 sm:mx-5" />
              </div>
              )}

              <div className="grid grid-cols-2 items-center justify-between gap-3 p-3 sm:gap-4 sm:p-4">
                <div className="relative flex min-w-0 justify-center">
                  {showAddStudentValidation && (
                    <span className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 shadow-sm">
                      Dodaj ucznia
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      addStudentRow();
                      setIsStudentPanelOpen(true);
                    }}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-full border border-sky-300 bg-sky-500 px-3 py-3 text-sm font-semibold text-white transition hover:bg-sky-600 sm:px-4 sm:text-base ${
                        studentRows.length === 0 && !hasAddedStudentInSession
                          ? "animate-pulse shadow-[0_0_0_4px_rgba(14,165,233,0.14),0_0_28px_rgba(14,165,233,0.45)] hover:shadow-[0_0_0_5px_rgba(14,165,233,0.18),0_0_36px_rgba(14,165,233,0.62)]"
                          : "shadow-sm hover:shadow-md"
                      }`}
                    >
                      <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold text-white">
                        {studentRows.length}
                      </span>
                      <Plus size={15} strokeWidth={2.2} />
                      Dodaj ucznia
                    </button>
                </div>

                <button
                  type="button"
                  onClick={handleGenerateButtonClick}
                  disabled={isGenerateDisabled}
                  aria-disabled={isGenerateButtonUnavailable}
                  className={`flex w-full shrink-0 items-center justify-center gap-2 rounded-full px-6 py-3 sm:text-base ${ACTION_BUTTON_TEXT_CLASS_NAME} ${
                    isGenerateButtonUnavailable
                      ? "cursor-not-allowed opacity-50"
                      : ""
                  }`}
                  style={getActionButtonStyle("orange")}
                >
                  <FileText size={16} strokeWidth={2.2} />
                  {isReviewVisible && reviewItems.length > 0
                    ? "Generuj ponownie"
                    : "Generuj"}
                </button>
              </div>
            </div>
          </div>

          {activePreviewItem && (
            <div className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
              <div className="flex h-[92vh] w-full max-w-6xl flex-col rounded-[28px] border border-white/70 bg-white p-4 shadow-[0_24px_80px_rgba(15,23,42,0.28)] sm:p-5">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Podgląd
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700">
                      {(previewModalIndex ?? 0) + 1} / {reviewItems.length}
                    </span>
                    <button
                      type="button"
                      onClick={closePreviewModal}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                      aria-label="Zamknij podgląd"
                    >
                      <X size={19} strokeWidth={2.3} />
                    </button>
                  </div>
                </div>

                <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[auto_minmax(0,1fr)_auto]">
                  <button
                    type="button"
                    onClick={() =>
                      handlePreviewNavigation((previewModalIndex ?? 0) - 1)
                    }
                    disabled={(previewModalIndex ?? 0) === 0}
                    className="hidden h-full min-h-[120px] w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40 lg:inline-flex"
                    aria-label="Poprzedni dokument"
                  >
                    <ChevronLeft size={24} strokeWidth={2.2} />
                  </button>

                  <div
                    className="relative min-h-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100"
                    onContextMenu={(event) => event.preventDefault()}
                  >
                    {previewPdfUrl ? (
                      isPreviewImage ? (
                        <img
                          src={previewPdfUrl}
                          alt="Podgląd certyfikatu"
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <iframe
                          src={`${previewPdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                          title="Podgląd PDF"
                          className="h-full w-full"
                        />
                      )
                    ) : (
                      <div className="flex h-full min-h-[420px] items-center justify-center text-sm font-semibold text-slate-500">
                        Generowanie podglądu
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      handlePreviewNavigation((previewModalIndex ?? 0) + 1)
                    }
                    disabled={
                      (previewModalIndex ?? 0) >= reviewItems.length - 1
                    }
                    className="hidden h-full min-h-[120px] w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40 lg:inline-flex"
                    aria-label="Następny dokument"
                  >
                    <ChevronRight size={24} strokeWidth={2.2} />
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 lg:hidden">
                  <button
                    type="button"
                    onClick={() =>
                      handlePreviewNavigation((previewModalIndex ?? 0) - 1)
                    }
                    disabled={(previewModalIndex ?? 0) === 0}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft size={17} strokeWidth={2.2} />
                    Poprzedni
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handlePreviewNavigation((previewModalIndex ?? 0) + 1)
                    }
                    disabled={
                      (previewModalIndex ?? 0) >= reviewItems.length - 1
                    }
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Następny
                    <ChevronRight size={17} strokeWidth={2.2} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificateGenerator;
